import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../../context/ThemeContext';
import { createChart, CandlestickSeries, AreaSeries, BaselineSeries } from 'lightweight-charts';
import DashboardLayout from '../../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { 
  TrendingUp, Clock, RefreshCw,
  ArrowUpRight, ArrowDownRight, Search,
  Maximize2, Minimize2, BarChart3, LineChart, Crosshair, Layers
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Asset logo URLs (using CoinGecko and public sources)
const ASSET_LOGOS = {
  // Crypto - jsdelivr CDN
  BTC: 'https://cdn.jsdelivr.net/gh/atomiclabs/cryptocurrency-icons@1a63530be6e374711a8554f31b17e4cb92c25fa5/128/color/btc.png',
  ETH: 'https://cdn.jsdelivr.net/gh/atomiclabs/cryptocurrency-icons@1a63530be6e374711a8554f31b17e4cb92c25fa5/128/color/eth.png',
  XRP: 'https://cdn.jsdelivr.net/gh/atomiclabs/cryptocurrency-icons@1a63530be6e374711a8554f31b17e4cb92c25fa5/128/color/xrp.png',
  SOL: 'https://cdn.jsdelivr.net/gh/atomiclabs/cryptocurrency-icons@1a63530be6e374711a8554f31b17e4cb92c25fa5/128/color/sol.png',
  DOGE: 'https://cdn.jsdelivr.net/gh/atomiclabs/cryptocurrency-icons@1a63530be6e374711a8554f31b17e4cb92c25fa5/128/color/doge.png',
  ADA: 'https://cdn.jsdelivr.net/gh/atomiclabs/cryptocurrency-icons@1a63530be6e374711a8554f31b17e4cb92c25fa5/128/color/ada.png',
  DOT: 'https://cdn.jsdelivr.net/gh/atomiclabs/cryptocurrency-icons@1a63530be6e374711a8554f31b17e4cb92c25fa5/128/color/dot.png',
  LINK: 'https://cdn.jsdelivr.net/gh/atomiclabs/cryptocurrency-icons@1a63530be6e374711a8554f31b17e4cb92c25fa5/128/color/link.png',
  AVAX: 'https://cdn.jsdelivr.net/gh/atomiclabs/cryptocurrency-icons@1a63530be6e374711a8554f31b17e4cb92c25fa5/128/color/avax.png',
  BNB: 'https://cdn.jsdelivr.net/gh/atomiclabs/cryptocurrency-icons@1a63530be6e374711a8554f31b17e4cb92c25fa5/128/color/bnb.png',
  MATIC: 'https://cdn.jsdelivr.net/gh/atomiclabs/cryptocurrency-icons@1a63530be6e374711a8554f31b17e4cb92c25fa5/128/color/matic.png',
  SHIB: 'https://cdn.jsdelivr.net/gh/atomiclabs/cryptocurrency-icons@1a63530be6e374711a8554f31b17e4cb92c25fa5/128/color/shib.png',
  LTC: 'https://cdn.jsdelivr.net/gh/atomiclabs/cryptocurrency-icons@1a63530be6e374711a8554f31b17e4cb92c25fa5/128/color/ltc.png',
  UNI: 'https://cdn.jsdelivr.net/gh/atomiclabs/cryptocurrency-icons@1a63530be6e374711a8554f31b17e4cb92c25fa5/128/color/uni.png',
  AAVE: 'https://cdn.jsdelivr.net/gh/atomiclabs/cryptocurrency-icons@1a63530be6e374711a8554f31b17e4cb92c25fa5/128/color/aave.png',
  XVG: 'https://cdn.jsdelivr.net/gh/atomiclabs/cryptocurrency-icons@1a63530be6e374711a8554f31b17e4cb92c25fa5/128/color/xvg.png',
  ATOM: 'https://cdn.jsdelivr.net/gh/atomiclabs/cryptocurrency-icons@1a63530be6e374711a8554f31b17e4cb92c25fa5/128/color/atom.png',
  FIL: 'https://cdn.jsdelivr.net/gh/atomiclabs/cryptocurrency-icons@1a63530be6e374711a8554f31b17e4cb92c25fa5/128/color/fil.png',
  NEAR: 'https://cdn.jsdelivr.net/gh/atomiclabs/cryptocurrency-icons@1a63530be6e374711a8554f31b17e4cb92c25fa5/128/color/near.png',
  OP: 'https://cdn.jsdelivr.net/gh/atomiclabs/cryptocurrency-icons@1a63530be6e374711a8554f31b17e4cb92c25fa5/128/color/op.png',
  ARB: 'https://cdn.jsdelivr.net/gh/atomiclabs/cryptocurrency-icons@1a63530be6e374711a8554f31b17e4cb92c25fa5/128/color/arb.png',
  INJ: 'https://cdn.jsdelivr.net/gh/atomiclabs/cryptocurrency-icons@1a63530be6e374711a8554f31b17e4cb92c25fa5/128/color/inj.png',
  FET: 'https://cdn.jsdelivr.net/gh/atomiclabs/cryptocurrency-icons@1a63530be6e374711a8554f31b17e4cb92c25fa5/128/color/fet.png',
  CRO: 'https://cdn.jsdelivr.net/gh/atomiclabs/cryptocurrency-icons@1a63530be6e374711a8554f31b17e4cb92c25fa5/128/color/cro.png',
  ALGO: 'https://cdn.jsdelivr.net/gh/atomiclabs/cryptocurrency-icons@1a63530be6e374711a8554f31b17e4cb92c25fa5/128/color/algo.png',
  VET: 'https://cdn.jsdelivr.net/gh/atomiclabs/cryptocurrency-icons@1a63530be6e374711a8554f31b17e4cb92c25fa5/128/color/vet.png',
  HBAR: 'https://cdn.jsdelivr.net/gh/atomiclabs/cryptocurrency-icons@1a63530be6e374711a8554f31b17e4cb92c25fa5/128/color/hbar.png',
  ICP: 'https://cdn.jsdelivr.net/gh/atomiclabs/cryptocurrency-icons@1a63530be6e374711a8554f31b17e4cb92c25fa5/128/color/icp.png',
  GRT: 'https://cdn.jsdelivr.net/gh/atomiclabs/cryptocurrency-icons@1a63530be6e374711a8554f31b17e4cb92c25fa5/128/color/grt.png',
  SAND: 'https://cdn.jsdelivr.net/gh/atomiclabs/cryptocurrency-icons@1a63530be6e374711a8554f31b17e4cb92c25fa5/128/color/sand.png',
  MANA: 'https://cdn.jsdelivr.net/gh/atomiclabs/cryptocurrency-icons@1a63530be6e374711a8554f31b17e4cb92c25fa5/128/color/mana.png',
  AXS: 'https://cdn.jsdelivr.net/gh/atomiclabs/cryptocurrency-icons@1a63530be6e374711a8554f31b17e4cb92c25fa5/128/color/axs.png',
  EGLD: 'https://cdn.jsdelivr.net/gh/atomiclabs/cryptocurrency-icons@1a63530be6e374711a8554f31b17e4cb92c25fa5/128/color/egld.png',
  XLM: 'https://cdn.jsdelivr.net/gh/atomiclabs/cryptocurrency-icons@1a63530be6e374711a8554f31b17e4cb92c25fa5/128/color/xlm.png',
  EOS: 'https://cdn.jsdelivr.net/gh/atomiclabs/cryptocurrency-icons@1a63530be6e374711a8554f31b17e4cb92c25fa5/128/color/eos.png',
  XTZ: 'https://cdn.jsdelivr.net/gh/atomiclabs/cryptocurrency-icons@1a63530be6e374711a8554f31b17e4cb92c25fa5/128/color/xtz.png',
  FLOW: 'https://cdn.jsdelivr.net/gh/atomiclabs/cryptocurrency-icons@1a63530be6e374711a8554f31b17e4cb92c25fa5/128/color/flow.png',
  ENS: 'https://cdn.jsdelivr.net/gh/atomiclabs/cryptocurrency-icons@1a63530be6e374711a8554f31b17e4cb92c25fa5/128/color/ens.png',
  CRV: 'https://cdn.jsdelivr.net/gh/atomiclabs/cryptocurrency-icons@1a63530be6e374711a8554f31b17e4cb92c25fa5/128/color/crv.png',
  MKR: 'https://cdn.jsdelivr.net/gh/atomiclabs/cryptocurrency-icons@1a63530be6e374711a8554f31b17e4cb92c25fa5/128/color/mkr.png',
  COMP: 'https://cdn.jsdelivr.net/gh/atomiclabs/cryptocurrency-icons@1a63530be6e374711a8554f31b17e4cb92c25fa5/128/color/comp.png',
  THETA: 'https://cdn.jsdelivr.net/gh/atomiclabs/cryptocurrency-icons@1a63530be6e374711a8554f31b17e4cb92c25fa5/128/color/theta.png',
  FTM: 'https://cdn.jsdelivr.net/gh/atomiclabs/cryptocurrency-icons@1a63530be6e374711a8554f31b17e4cb92c25fa5/128/color/ftm.png',
};

const ASSET_BRAND_COLORS = {
  // US Stocks
  'GOOGL': '#4285F4', 'AAPL': '#A2AAAD', 'MSFT': '#00A4EF', 'AMZN': '#FF9900',
  'TSLA': '#CC0000', 'META': '#0668E1', 'NVDA': '#76B900', 'NFLX': '#E50914',
  'JPM': '#003A70', 'DIS': '#0057A8', 'AMD': '#ED1C24', 'INTC': '#0071C5',
  'V': '#1A1F71', 'WMT': '#0071DC', 'PFE': '#0093D0', 'MRNA': '#00A1E0',
  'BIIB': '#00857C', 'JNJ': '#D51F37', 'BAC': '#012169', 'SPCX': '#000000',
  'KO': '#F40009', 'PEP': '#004B93', 'MA': '#EB001B', 'COST': '#E31837',
  'CRM': '#00A1E0', 'ADBE': '#FF0000', 'PYPL': '#003087', 'UBER': '#000000',
  'ABNB': '#FF5A5F', 'BA': '#0033A0', 'GS': '#6F9FD8', 'MS': '#002B5C',
  'BLK': '#000000', 'CSCO': '#049FD9', 'ORCL': '#C74634', 'IBM': '#0530AD',
  'COIN': '#0052FF', 'PLTR': '#101820', 'SPOT': '#1DB954', 'SHOP': '#96BF48',
  'NKE': '#111111', 'MCD': '#FFC72C', 'SBUX': '#00704A', 'F': '#003478',
  // Euronext
  'MC': '#2B2B2B', 'OR': '#1A1A1A', 'RMS': '#F37021', 'TTE': '#FF0000',
  'SAN': '#7B2D8E', 'AI': '#0055A4', 'SU': '#3DCD58', 'BNP': '#1B9E4B',
  'AIR': '#00205B', 'SAF': '#0050A0', 'DSY': '#005386', 'KER': '#1D1D1B',
  'CAP': '#0070AD', 'GLE': '#E30613', 'ACA': '#009639', 'ORA': '#FF7900',
  'ENGI': '#00AAFF', 'RNO': '#FFD700', 'CDI': '#1A1A1A', 'VIE': '#00A0E1',
  // Indices
  'SPX': '#1A73E8', 'NDX': '#00C853', 'DJI': '#1565C0', 'DAX': '#0046AA',
  'CAC40': '#0055A4', 'FTSE': '#C8102E', 'NIKKEI': '#BC002D', 'HSI': '#E74C3C',
  'VIX': '#FF6F00', 'RUSSELL': '#00838F', 'STOXX50': '#003399', 'SMI': '#FF0000',
  // Commodities
  'GOLD': '#FFD700', 'SILVER': '#C0C0C0', 'OIL': '#1a1a1a', 'BRENT': '#2C3E50',
  'NATGAS': '#FF6B35', 'WHEAT': '#DEB887', 'CORN': '#F4A460', 'COPPER': '#B87333',
  'PLAT': '#E5E4E2', 'PALL': '#CED0CE', 'COFFEE': '#6F4E37', 'COCOA': '#7B3F00',
  'SUGAR': '#FAFAFA', 'COTTON': '#F5F5DC', 'SOYBEAN': '#8B7355',
  'URANIUM': '#4CAF50', 'IRON': '#434343', 'ALUM': '#848789', 'ZINC': '#7D7D7D',
  'NICKEL': '#727472', 'TIN': '#A8A9AD', 'LEAD': '#848484',
  // Forex
  'EURUSD': '#003399', 'GBPUSD': '#012169', 'USDJPY': '#BC002D',
  'USDCHF': '#FF0000', 'AUDUSD': '#00008B', 'DXY': '#2E7D32',
  'EURGBP': '#003399', 'EURJPY': '#003399', 'GBPJPY': '#012169',
  // Biotech
  'GILD': '#003C71', 'REGN': '#FF6900', 'VRTX': '#00A3E0', 'AMGN': '#0063BE',
  'BMY': '#BE3A34', 'LLY': '#D52B1E', 'NVO': '#003D6B', 'AZN': '#2E008B',
  'GSK': '#F36633', 'CRSP': '#00B2A9', 'ILMN': '#40B5E5',
};

// Company domain mapping for logo URLs (via logo.clearbit.com)
const COMPANY_DOMAINS = {
  // US Stocks
  AAPL:'apple.com',GOOGL:'google.com',MSFT:'microsoft.com',AMZN:'amazon.com',TSLA:'tesla.com',META:'meta.com',
  NVDA:'nvidia.com',NFLX:'netflix.com',AMD:'amd.com',INTC:'intel.com',DIS:'disney.com',V:'visa.com',
  JPM:'jpmorganchase.com',WMT:'walmart.com',BAC:'bankofamerica.com',KO:'coca-cola.com',PEP:'pepsico.com',
  SPACEX:'spacex.com',SPCX:'spacex.com',
  MA:'mastercard.com',COST:'costco.com',CRM:'salesforce.com',ADBE:'adobe.com',PYPL:'paypal.com',
  UBER:'uber.com',ABNB:'airbnb.com',BA:'boeing.com',GS:'goldmansachs.com',MS:'morganstanley.com',
  BLK:'blackrock.com',T:'att.com',CSCO:'cisco.com',ORCL:'oracle.com',IBM:'ibm.com',QCOM:'qualcomm.com',
  TXN:'ti.com',NOW:'servicenow.com',ISRG:'intuitive.com',PANW:'paloaltonetworks.com',SNOW:'snowflake.com',
  SQ:'block.xyz',SHOP:'shopify.com',COIN:'coinbase.com',PLTR:'palantir.com',RIVN:'rivian.com',
  SPOT:'spotify.com',ROKU:'roku.com',ZM:'zoom.us',DDOG:'datadoghq.com',CRWD:'crowdstrike.com',
  NET:'cloudflare.com',MDB:'mongodb.com',LULU:'lululemon.com',HD:'homedepot.com',LOW:'lowes.com',
  UNH:'unitedhealthgroup.com',JNJ:'jnj.com',PFE:'pfizer.com',ABBV:'abbvie.com',MRK:'merck.com',
  XOM:'exxonmobil.com',CVX:'chevron.com',PG:'pg.com',NKE:'nike.com',MCD:'mcdonalds.com',
  SBUX:'starbucks.com',F:'ford.com',GM:'gm.com',AAL:'aa.com',DAL:'delta.com',
  // Biotech
  MRNA:'modernatx.com',BIIB:'biogen.com',GILD:'gilead.com',REGN:'regeneron.com',VRTX:'vrtx.com',
  AMGN:'amgen.com',BMY:'bms.com',LLY:'lilly.com',NVO:'novonordisk.com',AZN:'astrazeneca.com',
  GSK:'gsk.com',SNY:'sanofi.com',ILMN:'illumina.com',CRSP:'crisprtx.com',
  ALNY:'alnylam.com',INCY:'incyte.com',BMRN:'biomarin.com',EXEL:'exelixis.com',
  HALO:'halozyme.com',SRPT:'sarepta.com',IONS:'ionispharma.com',NBIX:'neurocrine.com',
  // Euronext
  MC:'lvmh.com',OR:'loreal.com',RMS:'hermes.com',TTE:'totalenergies.com',SAN:'sanofi.com',
  AI:'airliquide.com',SU:'se.com',BNP:'bnpparibas.com',AIR:'airbus.com',SAF:'safran-group.com',
  DSY:'3ds.com',KER:'kering.com',RI:'pernod-ricard.com',EL:'essilorluxottica.com',CAP:'capgemini.com',
  SGO:'saint-gobain.com',BN:'danone.com',ORA:'orange.com',GLE:'societegenerale.com',ACA:'credit-agricole.com',
  VIV:'vivendi.com',STMPA:'st.com',PUB:'publicisgroupe.com',LR:'legrand.com',ML:'michelin.com',
  HO:'thalesgroup.com',WLN:'worldline.com',RNO:'renaultgroup.com',VIE:'veolia.com',ALO:'alstom.com',
  ENGI:'engie.com',CA:'carrefour.com',DG:'vinci.com',CS:'axa.com',CDI:'dior.com',
  EN:'bouygues.com',FP:'totalenergies.com',URW:'urw.com',ATO:'atos.net',TEP:'teleperformance.com',
  SW:'sodexo.com',AKE:'arkema.com',MT:'arcelormittal.com',AM:'dassault-aviation.com',ERA:'eramet.com',
};

// Get logo URL for any asset
const getAssetLogo = (symbol) => {
  if (ASSET_LOGOS[symbol]) return ASSET_LOGOS[symbol];
  const domain = COMPANY_DOMAINS[symbol];
  if (domain) return `https://www.google.com/s2/favicons?domain=${domain}&sz=128`;
  return null;
};


// All assets with TradingView symbols for live charts
const FALLBACK_ASSETS = {
  crypto: [
    { symbol: 'BTC', name: 'Bitcoin', tv: 'BINANCE:BTCUSDT', price: 0, change: 0 },
    { symbol: 'ETH', name: 'Ethereum', tv: 'BINANCE:ETHUSDT', price: 0, change: 0 },
    { symbol: 'BNB', name: 'BNB', tv: 'BINANCE:BNBUSDT', price: 0, change: 0 },
    { symbol: 'XRP', name: 'Ripple', tv: 'BINANCE:XRPUSDT', price: 0, change: 0 },
    { symbol: 'SOL', name: 'Solana', tv: 'BINANCE:SOLUSDT', price: 0, change: 0 },
    { symbol: 'ADA', name: 'Cardano', tv: 'BINANCE:ADAUSDT', price: 0, change: 0 },
    { symbol: 'DOGE', name: 'Dogecoin', tv: 'BINANCE:DOGEUSDT', price: 0, change: 0 },
    { symbol: 'DOT', name: 'Polkadot', tv: 'BINANCE:DOTUSDT', price: 0, change: 0 },
    { symbol: 'AVAX', name: 'Avalanche', tv: 'BINANCE:AVAXUSDT', price: 0, change: 0 },
    { symbol: 'LINK', name: 'Chainlink', tv: 'BINANCE:LINKUSDT', price: 0, change: 0 },
    { symbol: 'MATIC', name: 'Polygon', tv: 'BINANCE:MATICUSDT', price: 0, change: 0 },
    { symbol: 'LTC', name: 'Litecoin', tv: 'BINANCE:LTCUSDT', price: 0, change: 0 },
    { symbol: 'UNI', name: 'Uniswap', tv: 'BINANCE:UNIUSDT', price: 0, change: 0 },
    { symbol: 'AAVE', name: 'Aave', tv: 'BINANCE:AAVEUSDT', price: 0, change: 0 },
    { symbol: 'ATOM', name: 'Cosmos', tv: 'BINANCE:ATOMUSDT', price: 0, change: 0 },
    { symbol: 'SHIB', name: 'Shiba Inu', tv: 'BINANCE:SHIBUSDT', price: 0, change: 0 },
    { symbol: 'XVG', name: 'Verge', tv: 'BINANCE:XVGUSDT', price: 0, change: 0 },
    { symbol: 'FIL', name: 'Filecoin', tv: 'BINANCE:FILUSDT', price: 0, change: 0 },
    { symbol: 'NEAR', name: 'NEAR Protocol', tv: 'BINANCE:NEARUSDT', price: 0, change: 0 },
    { symbol: 'APT', name: 'Aptos', tv: 'BINANCE:APTUSDT', price: 0, change: 0 },
    { symbol: 'OP', name: 'Optimism', tv: 'BINANCE:OPUSDT', price: 0, change: 0 },
    { symbol: 'ARB', name: 'Arbitrum', tv: 'BINANCE:ARBUSDT', price: 0, change: 0 },
    { symbol: 'INJ', name: 'Injective', tv: 'BINANCE:INJUSDT', price: 0, change: 0 },
    { symbol: 'RNDR', name: 'Render', tv: 'BINANCE:RNDRUSDT', price: 0, change: 0 },
    { symbol: 'FET', name: 'Fetch.ai', tv: 'BINANCE:FETUSDT', price: 0, change: 0 },
    { symbol: 'SUI', name: 'Sui', tv: 'BINANCE:SUIUSDT', price: 0, change: 0 },
    { symbol: 'SEI', name: 'Sei', tv: 'BINANCE:SEIUSDT', price: 0, change: 0 },
    { symbol: 'TIA', name: 'Celestia', tv: 'BINANCE:TIAUSDT', price: 0, change: 0 },
    { symbol: 'PEPE', name: 'Pepe', tv: 'BINANCE:PEPEUSDT', price: 0, change: 0 },
    { symbol: 'WIF', name: 'dogwifhat', tv: 'BINANCE:WIFUSDT', price: 0, change: 0 },
    { symbol: 'CRO', name: 'Cronos', tv: 'BINANCE:CROUSDT', price: 0, change: 0 },
    { symbol: 'ALGO', name: 'Algorand', tv: 'BINANCE:ALGOUSDT', price: 0, change: 0 },
    { symbol: 'VET', name: 'VeChain', tv: 'BINANCE:VETUSDT', price: 0, change: 0 },
    { symbol: 'HBAR', name: 'Hedera', tv: 'BINANCE:HBARUSDT', price: 0, change: 0 },
    { symbol: 'ICP', name: 'Internet Computer', tv: 'BINANCE:ICPUSDT', price: 0, change: 0 },
    { symbol: 'GRT', name: 'The Graph', tv: 'BINANCE:GRTUSDT', price: 0, change: 0 },
    { symbol: 'SAND', name: 'The Sandbox', tv: 'BINANCE:SANDUSDT', price: 0, change: 0 },
    { symbol: 'MANA', name: 'Decentraland', tv: 'BINANCE:MANAUSDT', price: 0, change: 0 },
    { symbol: 'AXS', name: 'Axie Infinity', tv: 'BINANCE:AXSUSDT', price: 0, change: 0 },
    { symbol: 'EGLD', name: 'MultiversX', tv: 'BINANCE:EGLDUSDT', price: 0, change: 0 },
    { symbol: 'XLM', name: 'Stellar', tv: 'BINANCE:XLMUSDT', price: 0, change: 0 },
    { symbol: 'EOS', name: 'EOS', tv: 'BINANCE:EOSUSDT', price: 0, change: 0 },
    { symbol: 'XTZ', name: 'Tezos', tv: 'BINANCE:XTZUSDT', price: 0, change: 0 },
    { symbol: 'FLOW', name: 'Flow', tv: 'BINANCE:FLOWUSDT', price: 0, change: 0 },
    { symbol: 'ENS', name: 'Ethereum Name', tv: 'BINANCE:ENSUSDT', price: 0, change: 0 },
    { symbol: 'CRV', name: 'Curve', tv: 'BINANCE:CRVUSDT', price: 0, change: 0 },
    { symbol: 'MKR', name: 'Maker', tv: 'BINANCE:MKRUSDT', price: 0, change: 0 },
    { symbol: 'COMP', name: 'Compound', tv: 'BINANCE:COMPUSDT', price: 0, change: 0 },
    { symbol: 'THETA', name: 'Theta', tv: 'BINANCE:THETAUSDT', price: 0, change: 0 },
    { symbol: 'FTM', name: 'Fantom', tv: 'BINANCE:FTMUSDT', price: 0, change: 0 },
  ],
  bourse: [
    { symbol: 'AAPL', name: 'Apple', tv: 'NASDAQ:AAPL', price: 0, change: 0 },
    { symbol: 'GOOGL', name: 'Alphabet', tv: 'NASDAQ:GOOGL', price: 0, change: 0 },
    { symbol: 'MSFT', name: 'Microsoft', tv: 'NASDAQ:MSFT', price: 0, change: 0 },
    { symbol: 'AMZN', name: 'Amazon', tv: 'NASDAQ:AMZN', price: 0, change: 0 },
    { symbol: 'TSLA', name: 'Tesla', tv: 'NASDAQ:TSLA', price: 0, change: 0 },
    { symbol: 'META', name: 'Meta', tv: 'NASDAQ:META', price: 0, change: 0 },
    { symbol: 'NVDA', name: 'NVIDIA', tv: 'NASDAQ:NVDA', price: 0, change: 0 },
    { symbol: 'NFLX', name: 'Netflix', tv: 'NASDAQ:NFLX', price: 0, change: 0 },
    { symbol: 'AMD', name: 'AMD', tv: 'NASDAQ:AMD', price: 0, change: 0 },
    { symbol: 'INTC', name: 'Intel', tv: 'NASDAQ:INTC', price: 0, change: 0 },
    { symbol: 'DIS', name: 'Disney', tv: 'NYSE:DIS', price: 0, change: 0 },
    { symbol: 'V', name: 'Visa', tv: 'NYSE:V', price: 0, change: 0 },
    { symbol: 'JPM', name: 'JPMorgan', tv: 'NYSE:JPM', price: 0, change: 0 },
    { symbol: 'WMT', name: 'Walmart', tv: 'NYSE:WMT', price: 0, change: 0 },
    { symbol: 'BAC', name: 'Bank of America', tv: 'NYSE:BAC', price: 0, change: 0 },
    { symbol: 'KO', name: 'Coca-Cola', tv: 'NYSE:KO', price: 0, change: 0 },
    { symbol: 'PEP', name: 'PepsiCo', tv: 'NASDAQ:PEP', price: 0, change: 0 },
    { symbol: 'MA', name: 'Mastercard', tv: 'NYSE:MA', price: 0, change: 0 },
    { symbol: 'COST', name: 'Costco', tv: 'NASDAQ:COST', price: 0, change: 0 },
    { symbol: 'CRM', name: 'Salesforce', tv: 'NYSE:CRM', price: 0, change: 0 },
    { symbol: 'ADBE', name: 'Adobe', tv: 'NASDAQ:ADBE', price: 0, change: 0 },
    { symbol: 'PYPL', name: 'PayPal', tv: 'NASDAQ:PYPL', price: 0, change: 0 },
    { symbol: 'UBER', name: 'Uber', tv: 'NYSE:UBER', price: 0, change: 0 },
    { symbol: 'ABNB', name: 'Airbnb', tv: 'NASDAQ:ABNB', price: 0, change: 0 },
    { symbol: 'BA', name: 'Boeing', tv: 'NYSE:BA', price: 0, change: 0 },
    { symbol: 'GS', name: 'Goldman Sachs', tv: 'NYSE:GS', price: 0, change: 0 },
    { symbol: 'MS', name: 'Morgan Stanley', tv: 'NYSE:MS', price: 0, change: 0 },
    { symbol: 'BLK', name: 'BlackRock', tv: 'NYSE:BLK', price: 0, change: 0 },
    { symbol: 'T', name: 'AT&T', tv: 'NYSE:T', price: 0, change: 0 },
    { symbol: 'CSCO', name: 'Cisco', tv: 'NASDAQ:CSCO', price: 0, change: 0 },
    { symbol: 'ORCL', name: 'Oracle', tv: 'NYSE:ORCL', price: 0, change: 0 },
    { symbol: 'IBM', name: 'IBM', tv: 'NYSE:IBM', price: 0, change: 0 },
    { symbol: 'QCOM', name: 'Qualcomm', tv: 'NASDAQ:QCOM', price: 0, change: 0 },
    { symbol: 'TXN', name: 'Texas Instruments', tv: 'NASDAQ:TXN', price: 0, change: 0 },
    { symbol: 'NOW', name: 'ServiceNow', tv: 'NYSE:NOW', price: 0, change: 0 },
    { symbol: 'ISRG', name: 'Intuitive Surgical', tv: 'NASDAQ:ISRG', price: 0, change: 0 },
    { symbol: 'PANW', name: 'Palo Alto Networks', tv: 'NASDAQ:PANW', price: 0, change: 0 },
    { symbol: 'SNOW', name: 'Snowflake', tv: 'NYSE:SNOW', price: 0, change: 0 },
    { symbol: 'SQ', name: 'Block (Square)', tv: 'NYSE:SQ', price: 0, change: 0 },
    { symbol: 'SHOP', name: 'Shopify', tv: 'NYSE:SHOP', price: 0, change: 0 },
    { symbol: 'COIN', name: 'Coinbase', tv: 'NASDAQ:COIN', price: 0, change: 0 },
    { symbol: 'PLTR', name: 'Palantir', tv: 'NYSE:PLTR', price: 0, change: 0 },
    { symbol: 'RIVN', name: 'Rivian', tv: 'NASDAQ:RIVN', price: 0, change: 0 },
    { symbol: 'SPOT', name: 'Spotify', tv: 'NYSE:SPOT', price: 0, change: 0 },
    { symbol: 'ROKU', name: 'Roku', tv: 'NASDAQ:ROKU', price: 0, change: 0 },
    { symbol: 'ZM', name: 'Zoom', tv: 'NASDAQ:ZM', price: 0, change: 0 },
    { symbol: 'DDOG', name: 'Datadog', tv: 'NASDAQ:DDOG', price: 0, change: 0 },
    { symbol: 'CRWD', name: 'CrowdStrike', tv: 'NASDAQ:CRWD', price: 0, change: 0 },
    { symbol: 'NET', name: 'Cloudflare', tv: 'NYSE:NET', price: 0, change: 0 },
    { symbol: 'MDB', name: 'MongoDB', tv: 'NASDAQ:MDB', price: 0, change: 0 },
    { symbol: 'LULU', name: 'Lululemon', tv: 'NASDAQ:LULU', price: 0, change: 0 },
    { symbol: 'HD', name: 'Home Depot', tv: 'NYSE:HD', price: 0, change: 0 },
    { symbol: 'LOW', name: "Lowe's", tv: 'NYSE:LOW', price: 0, change: 0 },
    { symbol: 'UNH', name: 'UnitedHealth', tv: 'NYSE:UNH', price: 0, change: 0 },
    { symbol: 'JNJ', name: 'Johnson & Johnson', tv: 'NYSE:JNJ', price: 0, change: 0 },
    { symbol: 'PFE', name: 'Pfizer', tv: 'NYSE:PFE', price: 0, change: 0 },
    { symbol: 'ABBV', name: 'AbbVie', tv: 'NYSE:ABBV', price: 0, change: 0 },
    { symbol: 'MRK', name: 'Merck', tv: 'NYSE:MRK', price: 0, change: 0 },
    { symbol: 'XOM', name: 'ExxonMobil', tv: 'NYSE:XOM', price: 0, change: 0 },
    { symbol: 'CVX', name: 'Chevron', tv: 'NYSE:CVX', price: 0, change: 0 },
    { symbol: 'PG', name: 'Procter & Gamble', tv: 'NYSE:PG', price: 0, change: 0 },
    { symbol: 'NKE', name: 'Nike', tv: 'NYSE:NKE', price: 0, change: 0 },
    { symbol: 'MCD', name: "McDonald's", tv: 'NYSE:MCD', price: 0, change: 0 },
    { symbol: 'SBUX', name: 'Starbucks', tv: 'NASDAQ:SBUX', price: 0, change: 0 },
    { symbol: 'F', name: 'Ford', tv: 'NYSE:F', price: 0, change: 0 },
    { symbol: 'GM', name: 'General Motors', tv: 'NYSE:GM', price: 0, change: 0 },
    { symbol: 'AAL', name: 'American Airlines', tv: 'NASDAQ:AAL', price: 0, change: 0 },
    { symbol: 'DAL', name: 'Delta Airlines', tv: 'NYSE:DAL', price: 0, change: 0 },
    { symbol: 'SPCX', name: 'SpaceX', tv: 'NASDAQ:SPCX', price: 0, change: 0 },
  ],
  euronext: [
    { symbol: 'MC', name: 'LVMH', tv: 'EURONEXT:MC', price: 0, change: 0 },
    { symbol: 'OR', name: "L'Oréal", tv: 'EURONEXT:OR', price: 0, change: 0 },
    { symbol: 'RMS', name: 'Hermès', tv: 'EURONEXT:RMS', price: 0, change: 0 },
    { symbol: 'TTE', name: 'TotalEnergies', tv: 'EURONEXT:TTE', price: 0, change: 0 },
    { symbol: 'SAN', name: 'Sanofi', tv: 'EURONEXT:SAN', price: 0, change: 0 },
    { symbol: 'AI', name: 'Air Liquide', tv: 'EURONEXT:AI', price: 0, change: 0 },
    { symbol: 'SU', name: 'Schneider Electric', tv: 'EURONEXT:SU', price: 0, change: 0 },
    { symbol: 'BNP', name: 'BNP Paribas', tv: 'EURONEXT:BNP', price: 0, change: 0 },
    { symbol: 'DG', name: 'Vinci', tv: 'EURONEXT:DG', price: 0, change: 0 },
    { symbol: 'AIR', name: 'Airbus', tv: 'EURONEXT:AIR', price: 0, change: 0 },
    { symbol: 'CS', name: 'AXA', tv: 'EURONEXT:CS', price: 0, change: 0 },
    { symbol: 'SAF', name: 'Safran', tv: 'EURONEXT:SAF', price: 0, change: 0 },
    { symbol: 'DSY', name: 'Dassault Systèmes', tv: 'EURONEXT:DSY', price: 0, change: 0 },
    { symbol: 'KER', name: 'Kering', tv: 'EURONEXT:KER', price: 0, change: 0 },
    { symbol: 'RI', name: 'Pernod Ricard', tv: 'EURONEXT:RI', price: 0, change: 0 },
    { symbol: 'EL', name: 'EssilorLuxottica', tv: 'EURONEXT:EL', price: 0, change: 0 },
    { symbol: 'CAP', name: 'Capgemini', tv: 'EURONEXT:CAP', price: 0, change: 0 },
    { symbol: 'SGO', name: 'Saint-Gobain', tv: 'EURONEXT:SGO', price: 0, change: 0 },
    { symbol: 'BN', name: 'Danone', tv: 'EURONEXT:BN', price: 0, change: 0 },
    { symbol: 'ORA', name: 'Orange', tv: 'EURONEXT:ORA', price: 0, change: 0 },
    { symbol: 'EN', name: 'Bouygues', tv: 'EURONEXT:EN', price: 0, change: 0 },
    { symbol: 'GLE', name: 'Société Générale', tv: 'EURONEXT:GLE', price: 0, change: 0 },
    { symbol: 'ACA', name: 'Crédit Agricole', tv: 'EURONEXT:ACA', price: 0, change: 0 },
    { symbol: 'VIV', name: 'Vivendi', tv: 'EURONEXT:VIV', price: 0, change: 0 },
    { symbol: 'STMPA', name: 'STMicroelectronics', tv: 'EURONEXT:STMPA', price: 0, change: 0 },
    { symbol: 'PUB', name: 'Publicis', tv: 'EURONEXT:PUB', price: 0, change: 0 },
    { symbol: 'LR', name: 'Legrand', tv: 'EURONEXT:LR', price: 0, change: 0 },
    { symbol: 'ML', name: 'Michelin', tv: 'EURONEXT:ML', price: 0, change: 0 },
    { symbol: 'HO', name: 'Thales', tv: 'EURONEXT:HO', price: 0, change: 0 },
    { symbol: 'FP', name: 'Total (ancien)', tv: 'EURONEXT:FP', price: 0, change: 0 },
    { symbol: 'WLN', name: 'Worldline', tv: 'EURONEXT:WLN', price: 0, change: 0 },
    { symbol: 'RNO', name: 'Renault', tv: 'EURONEXT:RNO', price: 0, change: 0 },
    { symbol: 'URW', name: 'Unibail-Rodamco', tv: 'EURONEXT:URW', price: 0, change: 0 },
    { symbol: 'VIE', name: 'Veolia', tv: 'EURONEXT:VIE', price: 0, change: 0 },
    { symbol: 'ATO', name: 'Atos', tv: 'EURONEXT:ATO', price: 0, change: 0 },
    { symbol: 'ALO', name: 'Alstom', tv: 'EURONEXT:ALO', price: 0, change: 0 },
    { symbol: 'TEP', name: 'Teleperformance', tv: 'EURONEXT:TEP', price: 0, change: 0 },
    { symbol: 'SW', name: 'Sodexo', tv: 'EURONEXT:SW', price: 0, change: 0 },
    { symbol: 'NK', name: 'Imerys', tv: 'EURONEXT:NK', price: 0, change: 0 },
    { symbol: 'ERA', name: 'Eramet', tv: 'EURONEXT:ERA', price: 0, change: 0 },
    { symbol: 'AKE', name: 'Arkema', tv: 'EURONEXT:AKE', price: 0, change: 0 },
    { symbol: 'MT', name: 'ArcelorMittal', tv: 'EURONEXT:MT', price: 0, change: 0 },
    { symbol: 'AM', name: 'Dassault Aviation', tv: 'EURONEXT:AM', price: 0, change: 0 },
    { symbol: 'CDI', name: 'Christian Dior', tv: 'EURONEXT:CDI', price: 0, change: 0 },
    { symbol: 'ENGI', name: 'Engie', tv: 'EURONEXT:ENGI', price: 0, change: 0 },
    { symbol: 'CA', name: 'Carrefour', tv: 'EURONEXT:CA', price: 0, change: 0 },
  ],
  indices: [
    { symbol: 'SPX', name: 'S&P 500', tv: 'FOREXCOM:SPXUSD', price: 0, change: 0 },
    { symbol: 'NDX', name: 'Nasdaq 100', tv: 'FOREXCOM:NSXUSD', price: 0, change: 0 },
    { symbol: 'DJI', name: 'Dow Jones', tv: 'FOREXCOM:DJI', price: 0, change: 0 },
    { symbol: 'DAX', name: 'DAX 40', tv: 'PEPPERSTONE:GER40', price: 0, change: 0 },
    { symbol: 'CAC40', name: 'CAC 40', tv: 'PEPPERSTONE:FRA40', price: 0, change: 0 },
    { symbol: 'FTSE', name: 'FTSE 100', tv: 'PEPPERSTONE:UK100', price: 0, change: 0 },
    { symbol: 'NIKKEI', name: 'Nikkei 225', tv: 'PEPPERSTONE:JPN225', price: 0, change: 0 },
    { symbol: 'HSI', name: 'Hang Seng', tv: 'PEPPERSTONE:HK50', price: 0, change: 0 },
    { symbol: 'VIX', name: 'Volatilité VIX', tv: 'TVC:VIX', price: 0, change: 0 },
    { symbol: 'RUSSELL', name: 'Russell 2000', tv: 'FOREXCOM:RUSSELLUSD', price: 0, change: 0 },
    { symbol: 'IBEX', name: 'IBEX 35', tv: 'PEPPERSTONE:ESP35', price: 0, change: 0 },
    { symbol: 'SMI', name: 'Swiss Market', tv: 'PEPPERSTONE:SUI20', price: 0, change: 0 },
    { symbol: 'STOXX50', name: 'Euro Stoxx 50', tv: 'PEPPERSTONE:EUSTX50', price: 0, change: 0 },
    { symbol: 'FTSEMIB', name: 'FTSE MIB', tv: 'PEPPERSTONE:ITA40', price: 0, change: 0 },
    { symbol: 'AEX', name: 'AEX 25', tv: 'PEPPERSTONE:NETH25', price: 0, change: 0 },
    { symbol: 'ASX200', name: 'ASX 200', tv: 'PEPPERSTONE:AUS200', price: 0, change: 0 },
    { symbol: 'KOSPI', name: 'KOSPI', tv: 'KRX:KOSPI', price: 0, change: 0 },
    { symbol: 'SENSEX', name: 'Sensex', tv: 'BSE:SENSEX', price: 0, change: 0 },
    { symbol: 'SHANGHAI', name: 'Shanghai Comp.', tv: 'SSE:000001', price: 0, change: 0 },
    { symbol: 'TSX', name: 'TSX Composite', tv: 'TSX:TSX', price: 0, change: 0 },
    { symbol: 'BOVESPA', name: 'Bovespa', tv: 'BMFBOVESPA:IBOV', price: 0, change: 0 },
    { symbol: 'BIST100', name: 'BIST 100', tv: 'BIST:XU100', price: 0, change: 0 },
  ],
  matiere: [
    { symbol: 'GOLD', name: 'Or (XAU/EUR)', tv: 'TVC:GOLD', price: 0, change: 0 },
    { symbol: 'SILVER', name: 'Argent (XAG)', tv: 'TVC:SILVER', price: 0, change: 0 },
    { symbol: 'OIL', name: 'Pétrole WTI', tv: 'TVC:USOIL', price: 0, change: 0 },
    { symbol: 'BRENT', name: 'Pétrole Brent', tv: 'TVC:UKOIL', price: 0, change: 0 },
    { symbol: 'NATGAS', name: 'Gaz Naturel', tv: 'TVC:NATGAS', price: 0, change: 0 },
    { symbol: 'WHEAT', name: 'Blé', tv: 'CBOT:ZW1!', price: 0, change: 0 },
    { symbol: 'CORN', name: 'Maïs', tv: 'CBOT:ZC1!', price: 0, change: 0 },
    { symbol: 'COPPER', name: 'Cuivre', tv: 'COMEX:HG1!', price: 0, change: 0 },
    { symbol: 'PLAT', name: 'Platine', tv: 'TVC:PLATINUM', price: 0, change: 0 },
    { symbol: 'PALL', name: 'Palladium', tv: 'TVC:PALLADIUM', price: 0, change: 0 },
    { symbol: 'SOYBEAN', name: 'Soja', tv: 'CBOT:ZS1!', price: 0, change: 0 },
    { symbol: 'SUGAR', name: 'Sucre', tv: 'ICEUSA:SB1!', price: 0, change: 0 },
    { symbol: 'COFFEE', name: 'Café Arabica', tv: 'ICEUSA:KC1!', price: 0, change: 0 },
    { symbol: 'COCOA', name: 'Cacao', tv: 'ICEUSA:CC1!', price: 0, change: 0 },
    { symbol: 'COTTON', name: 'Coton', tv: 'ICEUSA:CT1!', price: 0, change: 0 },
    { symbol: 'RICE', name: 'Riz', tv: 'CBOT:ZR1!', price: 0, change: 0 },
    { symbol: 'LUMBER', name: 'Bois', tv: 'CME:LBS1!', price: 0, change: 0 },
    { symbol: 'URANIUM', name: 'Uranium', tv: 'COMEX:UX1!', price: 0, change: 0 },
    { symbol: 'IRON', name: 'Minerai de Fer', tv: 'SGX:FEF1!', price: 0, change: 0 },
    { symbol: 'ALUM', name: 'Aluminium', tv: 'LME:AA1!', price: 0, change: 0 },
    { symbol: 'ZINC', name: 'Zinc', tv: 'LME:ZS1!', price: 0, change: 0 },
    { symbol: 'NICKEL', name: 'Nickel', tv: 'LME:NI1!', price: 0, change: 0 },
    { symbol: 'TIN', name: 'Étain', tv: 'LME:SN1!', price: 0, change: 0 },
    { symbol: 'LEAD', name: 'Plomb', tv: 'LME:PB1!', price: 0, change: 0 },
    { symbol: 'ROBUSTA', name: 'Café Robusta', tv: 'ICEEUR:RC1!', price: 0, change: 0 },
    { symbol: 'OJ', name: 'Jus d\'Orange', tv: 'ICEUSA:OJ1!', price: 0, change: 0 },
    { symbol: 'OATS', name: 'Avoine', tv: 'CBOT:ZO1!', price: 0, change: 0 },
    { symbol: 'LIVECATTLE', name: 'Bétail', tv: 'CME:LE1!', price: 0, change: 0 },
    { symbol: 'FEEDER', name: 'Bétail Feeder', tv: 'CME:GF1!', price: 0, change: 0 },
    { symbol: 'LHOG', name: 'Porc Maigre', tv: 'CME:HE1!', price: 0, change: 0 },
  ],
  devise: [
    { symbol: 'EURUSD', name: 'EUR/USD', tv: 'FX:EURUSD', price: 0, change: 0 },
    { symbol: 'GBPUSD', name: 'GBP/USD', tv: 'FX:GBPUSD', price: 0, change: 0 },
    { symbol: 'USDJPY', name: 'USD/JPY', tv: 'FX:USDJPY', price: 0, change: 0 },
    { symbol: 'USDCHF', name: 'USD/CHF', tv: 'FX:USDCHF', price: 0, change: 0 },
    { symbol: 'AUDUSD', name: 'AUD/USD', tv: 'FX:AUDUSD', price: 0, change: 0 },
    { symbol: 'USDCAD', name: 'USD/CAD', tv: 'FX:USDCAD', price: 0, change: 0 },
    { symbol: 'EURGBP', name: 'EUR/GBP', tv: 'FX:EURGBP', price: 0, change: 0 },
    { symbol: 'EURJPY', name: 'EUR/JPY', tv: 'FX:EURJPY', price: 0, change: 0 },
    { symbol: 'GBPJPY', name: 'GBP/JPY', tv: 'FX:GBPJPY', price: 0, change: 0 },
    { symbol: 'NZDUSD', name: 'NZD/USD', tv: 'FX:NZDUSD', price: 0, change: 0 },
    { symbol: 'DXY', name: 'Dollar Index', tv: 'TVC:DXY', price: 0, change: 0 },
    { symbol: 'EURCHF', name: 'EUR/CHF', tv: 'FX:EURCHF', price: 0, change: 0 },
    { symbol: 'EURAUD', name: 'EUR/AUD', tv: 'FX:EURAUD', price: 0, change: 0 },
    { symbol: 'EURCAD', name: 'EUR/CAD', tv: 'FX:EURCAD', price: 0, change: 0 },
    { symbol: 'EURNZD', name: 'EUR/NZD', tv: 'FX:EURNZD', price: 0, change: 0 },
    { symbol: 'GBPCHF', name: 'GBP/CHF', tv: 'FX:GBPCHF', price: 0, change: 0 },
    { symbol: 'GBPAUD', name: 'GBP/AUD', tv: 'FX:GBPAUD', price: 0, change: 0 },
    { symbol: 'GBPCAD', name: 'GBP/CAD', tv: 'FX:GBPCAD', price: 0, change: 0 },
    { symbol: 'AUDCAD', name: 'AUD/CAD', tv: 'FX:AUDCAD', price: 0, change: 0 },
    { symbol: 'AUDJPY', name: 'AUD/JPY', tv: 'FX:AUDJPY', price: 0, change: 0 },
    { symbol: 'AUDNZD', name: 'AUD/NZD', tv: 'FX:AUDNZD', price: 0, change: 0 },
    { symbol: 'CADJPY', name: 'CAD/JPY', tv: 'FX:CADJPY', price: 0, change: 0 },
    { symbol: 'CHFJPY', name: 'CHF/JPY', tv: 'FX:CHFJPY', price: 0, change: 0 },
    { symbol: 'NZDJPY', name: 'NZD/JPY', tv: 'FX:NZDJPY', price: 0, change: 0 },
    { symbol: 'USDTRY', name: 'USD/TRY', tv: 'FX:USDTRY', price: 0, change: 0 },
    { symbol: 'USDZAR', name: 'USD/ZAR', tv: 'FX:USDZAR', price: 0, change: 0 },
    { symbol: 'USDMXN', name: 'USD/MXN', tv: 'FX:USDMXN', price: 0, change: 0 },
    { symbol: 'USDSGD', name: 'USD/SGD', tv: 'FX:USDSGD', price: 0, change: 0 },
    { symbol: 'USDHKD', name: 'USD/HKD', tv: 'FX:USDHKD', price: 0, change: 0 },
    { symbol: 'USDNOK', name: 'USD/NOK', tv: 'FX:USDNOK', price: 0, change: 0 },
    { symbol: 'USDSEK', name: 'USD/SEK', tv: 'FX:USDSEK', price: 0, change: 0 },
  ],
  biotech: [
    { symbol: 'MRNA', name: 'Moderna', tv: 'NASDAQ:MRNA', price: 0, change: 0 },
    { symbol: 'PFE', name: 'Pfizer', tv: 'NYSE:PFE', price: 0, change: 0 },
    { symbol: 'BIIB', name: 'Biogen', tv: 'NASDAQ:BIIB', price: 0, change: 0 },
    { symbol: 'JNJ', name: 'Johnson & J.', tv: 'NYSE:JNJ', price: 0, change: 0 },
    { symbol: 'GILD', name: 'Gilead Sciences', tv: 'NASDAQ:GILD', price: 0, change: 0 },
    { symbol: 'ABBV', name: 'AbbVie', tv: 'NYSE:ABBV', price: 0, change: 0 },
    { symbol: 'REGN', name: 'Regeneron', tv: 'NASDAQ:REGN', price: 0, change: 0 },
    { symbol: 'VRTX', name: 'Vertex Pharma', tv: 'NASDAQ:VRTX', price: 0, change: 0 },
    { symbol: 'AMGN', name: 'Amgen', tv: 'NASDAQ:AMGN', price: 0, change: 0 },
    { symbol: 'BMY', name: 'Bristol-Myers', tv: 'NYSE:BMY', price: 0, change: 0 },
    { symbol: 'LLY', name: 'Eli Lilly', tv: 'NYSE:LLY', price: 0, change: 0 },
    { symbol: 'NVO', name: 'Novo Nordisk', tv: 'NYSE:NVO', price: 0, change: 0 },
    { symbol: 'AZN', name: 'AstraZeneca', tv: 'NASDAQ:AZN', price: 0, change: 0 },
    { symbol: 'GSK', name: 'GSK', tv: 'NYSE:GSK', price: 0, change: 0 },
    { symbol: 'SNY', name: 'Sanofi (US)', tv: 'NASDAQ:SNY', price: 0, change: 0 },
    { symbol: 'ILMN', name: 'Illumina', tv: 'NASDAQ:ILMN', price: 0, change: 0 },
    { symbol: 'ALNY', name: 'Alnylam Pharma', tv: 'NASDAQ:ALNY', price: 0, change: 0 },
    { symbol: 'SGEN', name: 'Seagen', tv: 'NASDAQ:SGEN', price: 0, change: 0 },
    { symbol: 'INCY', name: 'Incyte', tv: 'NASDAQ:INCY', price: 0, change: 0 },
    { symbol: 'BMRN', name: 'BioMarin', tv: 'NASDAQ:BMRN', price: 0, change: 0 },
    { symbol: 'EXEL', name: 'Exelixis', tv: 'NASDAQ:EXEL', price: 0, change: 0 },
    { symbol: 'HALO', name: 'Halozyme', tv: 'NASDAQ:HALO', price: 0, change: 0 },
    { symbol: 'SRPT', name: 'Sarepta', tv: 'NASDAQ:SRPT', price: 0, change: 0 },
    { symbol: 'RARE', name: 'Ultragenyx', tv: 'NASDAQ:RARE', price: 0, change: 0 },
    { symbol: 'IONS', name: 'Ionis Pharma', tv: 'NASDAQ:IONS', price: 0, change: 0 },
    { symbol: 'NBIX', name: 'Neurocrine Bio', tv: 'NASDAQ:NBIX', price: 0, change: 0 },
    { symbol: 'UTHR', name: 'United Therapeutics', tv: 'NASDAQ:UTHR', price: 0, change: 0 },
    { symbol: 'TECH', name: 'Bio-Techne', tv: 'NASDAQ:TECH', price: 0, change: 0 },
    { symbol: 'SYNA', name: 'Synaptive Medical', tv: 'NASDAQ:SYNA', price: 0, change: 0 },
    { symbol: 'CRSP', name: 'CRISPR Therapeutics', tv: 'NASDAQ:CRSP', price: 0, change: 0 },
  ],
};

// Asset names mapping — built from FALLBACK_ASSETS
const ASSET_NAMES = {};
Object.values(FALLBACK_ASSETS).forEach(list => list.forEach(a => { ASSET_NAMES[a.symbol] = a.name; }));


// TradingView Widget — iframe embed (Euronext uses Yahoo Finance chart fallback)
function TradingViewWidget({ symbol, theme = 'dark', livePrice = 0, chartStyle = 'candlestick', period = '1mo', fullscreen = false }) {
  const tvSymbolMap = {};
  Object.values(FALLBACK_ASSETS).forEach(cat => cat.forEach(a => { tvSymbolMap[a.symbol] = a.tv; }));
  const tvSymbol = tvSymbolMap[symbol] || `BINANCE:${symbol}USDT`;
  const containerRef = useRef(null);
  const loadedSymbol = useRef(null);
  const candleSeriesRef = useRef(null);
  const lastCandleRef = useRef(null);
  const chartDisposed = useRef(false);
  const isEuronext = tvSymbol.startsWith('EURONEXT:');

  useEffect(() => {
    if (!containerRef.current) return;
    if (loadedSymbol.current === tvSymbol) return;
    loadedSymbol.current = tvSymbol;
    candleSeriesRef.current = null;
    lastCandleRef.current = null;
    const abortCtrl = new AbortController();
    const signal = abortCtrl.signal;

    if (isEuronext) {
      if (!createChart) {
        containerRef.current.innerHTML = '<div style="display:flex;align-items:center;justify-content:center;height:100%;color:#9ca3af;font-size:14px;">Graphique non disponible sur ce navigateur</div>';
        return;
      }
      containerRef.current.innerHTML = '';
      try {
        const chartDiv = document.createElement('div');
      chartDiv.style.cssText = 'width:100%;height:100%;';
      containerRef.current.appendChild(chartDiv);
      
      const chart = createChart(chartDiv, { 
        width: chartDiv.clientWidth, height: fullscreen ? window.innerHeight - 50 : 400,
        layout: { background: { color: '#131722' }, textColor: '#d1d5db' },
        grid: { vertLines: { color: '#1e222d' }, horzLines: { color: '#1e222d' } },
        timeScale: { timeVisible: true, borderColor: '#2a2e39' },
        rightPriceScale: { borderColor: '#2a2e39' },
      });
      chartDisposed.current = false;
      
      // Chart style depends on prop
      const series = chartStyle === 'candlestick' 
        ? chart.addSeries(CandlestickSeries, {
            upColor: '#22c55e', downColor: '#ef4444',
            borderUpColor: '#22c55e', borderDownColor: '#ef4444',
            wickUpColor: '#22c55e', wickDownColor: '#ef4444',
            priceFormat: { type: 'price', precision: 2, minMove: 0.01 },
          })
        : chart.addSeries(BaselineSeries, {
            baseValue: { type: 'price', price: 0 },
            topLineColor: '#22c55e', topFillColor1: 'rgba(34, 197, 94, 0.28)', topFillColor2: 'rgba(34, 197, 94, 0.02)',
            bottomLineColor: '#ef4444', bottomFillColor1: 'rgba(239, 68, 68, 0.02)', bottomFillColor2: 'rgba(239, 68, 68, 0.28)',
            lineWidth: 2, crosshairMarkerVisible: true, crosshairMarkerRadius: 4,
            priceFormat: { type: 'price', precision: 2, minMove: 0.01 },
          });
      candleSeriesRef.current = series;
      
      fetch(`${API}/market/chart/${symbol}?period=${period}`, { signal })
        .then(r => r.json())
        .then(data => {
          if (chartDisposed.current || signal.aborted) return;
          if (data.candles?.length > 0) {
            if (chartStyle === 'candlestick') {
              series.setData(data.candles);
            } else {
              const lineData = data.candles.map(c => ({ time: c.time, value: c.close }));
              series.applyOptions({ baseValue: { type: 'price', price: lineData[0].value } });
              series.setData(lineData);
            }
            lastCandleRef.current = data.candles[data.candles.length - 1];
            chart.timeScale().fitContent();
          }
        }).catch(() => {});
      
      const ro = new ResizeObserver(() => { if (!chartDisposed.current) { try { chart.applyOptions({ width: chartDiv.clientWidth }); } catch {} } });
      ro.observe(chartDiv);
      
      return () => { 
        abortCtrl.abort();
        ro.disconnect(); 
        candleSeriesRef.current = null; 
        loadedSymbol.current = null;
        chartDisposed.current = true; try { chart.remove(); } catch {}
        
      };
      } catch (e) {
        if (containerRef.current) containerRef.current.innerHTML = '';
      }
    } else {
      // Lightweight Charts Baseline for ALL assets (replaces TradingView widget)
      if (!createChart) {
        containerRef.current.innerHTML = '<div style="display:flex;align-items:center;justify-content:center;height:100%;color:#9ca3af;font-size:14px;">Graphique non disponible</div>';
        return;
      }
      containerRef.current.innerHTML = '';
      try {
        const chartDiv = document.createElement('div');
        chartDiv.style.cssText = 'width:100%;height:100%;';
        containerRef.current.appendChild(chartDiv);
        
        const chart = createChart(chartDiv, { 
          width: chartDiv.clientWidth, height: fullscreen ? window.innerHeight - 50 : 400,
          layout: { background: { color: '#131722' }, textColor: '#d1d5db' },
          grid: { vertLines: { color: '#1e222d' }, horzLines: { color: '#1e222d' } },
          timeScale: { timeVisible: true, borderColor: '#2a2e39' },
          rightPriceScale: { borderColor: '#2a2e39' },
          crosshair: { mode: 0 },
        });
        chartDisposed.current = false;
        
        const series = chartStyle === 'candlestick'
          ? chart.addSeries(CandlestickSeries, {
              upColor: '#22c55e', downColor: '#ef4444',
              borderUpColor: '#22c55e', borderDownColor: '#ef4444',
              wickUpColor: '#22c55e', wickDownColor: '#ef4444',
              priceFormat: { type: 'price', precision: 2, minMove: 0.01 },
            })
          : chart.addSeries(BaselineSeries, {
              baseValue: { type: 'price', price: 0 },
              topLineColor: '#22c55e', topFillColor1: 'rgba(34, 197, 94, 0.28)', topFillColor2: 'rgba(34, 197, 94, 0.02)',
              bottomLineColor: '#ef4444', bottomFillColor1: 'rgba(239, 68, 68, 0.02)', bottomFillColor2: 'rgba(239, 68, 68, 0.28)',
              lineWidth: 2, crosshairMarkerVisible: true, crosshairMarkerRadius: 4,
              priceFormat: { type: 'price', precision: 2, minMove: 0.01 },
            });
        candleSeriesRef.current = series;
        
        // Fetch chart data - map frontend symbols to Yahoo Finance symbols
        const chartSymbolMap = {
          // Commodities
          'GOLD': 'GC=F', 'SILVER': 'SI=F', 'OIL': 'CL=F', 'BRENT': 'BZ=F', 'PLAT': 'PL=F', 'PALL': 'PA=F',
          'COPPER': 'HG=F', 'NATGAS': 'NG=F', 'ALUM': 'ALI=F', 'NICKEL': 'NI=F', 'ZINC': 'ZN=F', 'TIN': 'SN=F',
          'LEAD': 'PB=F', 'IRON': 'TIO=F', 'URANIUM': 'UX=F', 'LUMBER': 'LBS=F', 'WHEAT': 'ZW=F', 'CORN': 'ZC=F',
          'SOYBEAN': 'ZS=F', 'COFFEE': 'KC=F', 'COCOA': 'CC=F', 'SUGAR': 'SB=F', 'COTTON': 'CT=F', 'OJ': 'OJ=F',
          'OATS': 'ZO=F', 'RICE': 'ZR=F', 'ROBUSTA': 'RC=F', 'LIVECATTLE': 'LE=F', 'FEEDER': 'GF=F', 'LHOG': 'HE=F',
          // Indices
          'SPX': '^GSPC', 'NDX': '^NDX', 'DJI': '^DJI', 'RUSSELL': '^RUT', 'FTSE': '^FTSE', 'DAX': '^GDAXI',
          'CAC': '^FCHI', 'CAC40': '^FCHI', 'NIKKEI': '^N225', 'HANGSENG': '^HSI', 'HSI': '^HSI', 'ASX200': '^AXJO',
          'STOXX50': '^STOXX50E', 'FTSEMIB': 'FTSEMIB.MI', 'IBEX': '^IBEX', 'SMI': '^SSMI', 'AEX': '^AEX',
          'BOVESPA': '^BVSP', 'TSX': '^GSPTSE', 'KOSPI': '^KS11', 'SENSEX': '^BSESN', 'SHANGHAI': '000001.SS',
          'BIST100': 'XU100.IS', 'NK': '^N225', 'VIX': '^VIX', 'DXY': 'DX-Y.NYB',
          // Forex
          'EURUSD': 'EURUSD=X', 'GBPUSD': 'GBPUSD=X', 'USDJPY': 'USDJPY=X', 'USDCHF': 'USDCHF=X',
          'AUDUSD': 'AUDUSD=X', 'NZDUSD': 'NZDUSD=X', 'USDCAD': 'USDCAD=X', 'USDSEK': 'USDSEK=X',
          'USDNOK': 'USDNOK=X', 'USDHKD': 'USDHKD=X', 'USDSGD': 'USDSGD=X', 'EURGBP': 'EURGBP=X',
          'EURJPY': 'EURJPY=X', 'EURCHF': 'EURCHF=X', 'EURAUD': 'EURAUD=X', 'EURCAD': 'EURCAD=X',
          'EURNZD': 'EURNZD=X', 'GBPJPY': 'GBPJPY=X', 'GBPCHF': 'GBPCHF=X', 'GBPAUD': 'GBPAUD=X',
          'GBPCAD': 'GBPCAD=X', 'AUDJPY': 'AUDJPY=X', 'AUDNZD': 'AUDNZD=X', 'AUDCAD': 'AUDCAD=X',
          'NZDJPY': 'NZDJPY=X', 'CADJPY': 'CADJPY=X', 'CHFJPY': 'CHFJPY=X', 'USDTRY': 'USDTRY=X',
          'USDMXN': 'USDMXN=X', 'USDZAR': 'USDZAR=X',
          // Euronext
          'MC': 'MC.PA', 'OR': 'OR.PA', 'AI': 'AI.PA', 'SU': 'SU.PA', 'SAN': 'SAN.PA', 'BNP': 'BNP.PA',
          'GLE': 'GLE.PA', 'CAP': 'CAP.PA', 'SAF': 'SAF.PA', 'AIR': 'AIR.PA', 'RMS': 'RMS.PA', 'KER': 'KER.PA',
          'EL': 'EL.PA', 'RI': 'RI.PA', 'RNO': 'RNO.PA', 'BN': 'BN.PA', 'ACA': 'ACA.PA', 'CS': 'CS.PA',
          'EN': 'EN.PA', 'ENGI': 'ENGI.PA', 'VIV': 'VIV.PA', 'FP': 'FP.PA', 'TTE': 'TTE.PA', 'PUB': 'PUB.PA',
          'SGO': 'SGO.PA', 'DSY': 'DSY.PA', 'ORA': 'ORA.PA', 'VIE': 'VIE.PA', 'AM': 'AM.PA', 'ERA': 'ERA.PA',
          'ATO': 'ATO.PA', 'HO': 'HO.PA', 'URW': 'URW.PA', 'SW': 'SW.PA', 'WLN': 'WLN.PA', 'CDI': 'CDI.PA',
          'TEP': 'TEP.PA', 'ML': 'ML.PA', 'ALO': 'ALO.PA', 'AKE': 'AKE.PA', 'LR': 'LR.PA', 'MT': 'MT.PA',
          'STMPA': 'STMPA.PA', 'TECH': 'TECH.PA', 'CA': 'CA.PA',
          // SpaceX
          'SPCX': 'SPCX', 'SPACEX': 'SPCX',
        };
        const cryptoRemap = {'UNI': 'UNI7083-USD', 'APT': 'APT21794-USD', 'SUI': 'SUI20947-USD', 'GRT': 'GRT6719-USD', 'RNDR': 'RENDER-USD', 'PEPE': 'PEPE24478-USD', 'MATIC': 'WMATIC-USD', 'TECH': 'TECH-USD'};
        const specialRemap = {'MT': 'MT.AS', 'SQ': 'XYZ', 'FP': 'TTE.PA', 'NICKEL': 'NICK.L', 'TIN': 'TIN.L', 'LEAD': 'LEAD.L', 'URANIUM': 'URA', 'ROBUSTA': 'RB=F', 'LUMBER': 'LB=F', 'SGEN': 'DAIICHI4568.T'};
        const cryptoSymbols = ['BTC','ETH','BNB','SOL','XRP','ADA','DOGE','DOT','AVAX','LINK','ATOM','AAVE','INJ','NEAR','ALGO','CRV','LDO','ARB','OP','SEI','TIA','WIF','CRO','XVG','SHIB','LTC','XLM','EOS','XTZ','FLOW','ENS','MKR','THETA','EGLD','MANA','SAND','AXS','VET','HBAR','ICP','FIL','FET'];
        
        let ySymbol;
        if (cryptoRemap[symbol]) ySymbol = cryptoRemap[symbol];
        else if (specialRemap[symbol]) ySymbol = specialRemap[symbol];
        else if (chartSymbolMap[symbol]) ySymbol = chartSymbolMap[symbol];
        else if (symbol.includes('.') || symbol.includes('=') || symbol.includes('^') || symbol.includes('-')) ySymbol = symbol;
        else if (cryptoSymbols.includes(symbol)) ySymbol = `${symbol}-USD`;
        else ySymbol = symbol;
        
        fetch(`${API}/market/chart/${ySymbol}?period=${period}`, { signal })
          .then(r => r.json())
          .then(data => {
            if (chartDisposed.current || signal.aborted) return;
            if (data.candles?.length > 0) {
              // If only 1-2 candles (newly listed stock), generate synthetic data for visual
              let candles = data.candles;
              if (candles.length < 3) {
                const c = candles[candles.length - 1];
                const now = c.time;
                const synth = [];
                const basePrice = c.open || c.close * 0.95;
                for (let i = 10; i >= 1; i--) {
                  const t = now - (i * 86400);
                  const noise = (Math.random() - 0.5) * (c.close * 0.03);
                  const progress = (10 - i) / 10;
                  const p = basePrice + (c.close - basePrice) * progress + noise;
                  synth.push({ time: t, open: p - Math.random() * 2, high: p + Math.random() * 3, low: p - Math.random() * 3, close: p });
                }
                synth.push(c);
                candles = synth;
              }
              // Get EUR rate for conversion (non-Euronext assets are in USD)
              fetch(`${API}/market/prices`, { signal })
                .then(r2 => r2.json())
                .then(mdata => {
                  if (chartDisposed.current || signal.aborted) return;
                  const eurRate = mdata.eur_rate || 1.08;
                  const isEurAsset = symbol.includes('.PA') || symbol.includes('.AS') || symbol.includes('.BR');
                  if (chartStyle === 'candlestick') {
                    const candleData = candles.map(c => ({
                      time: c.time,
                      open: isEurAsset ? c.open : (c.open / eurRate),
                      high: isEurAsset ? c.high : (c.high / eurRate),
                      low: isEurAsset ? c.low : (c.low / eurRate),
                      close: isEurAsset ? c.close : (c.close / eurRate),
                    }));
                    series.setData(candleData);
                  } else {
                    const lineData = candles.map(c => ({
                      time: c.time,
                      value: isEurAsset ? c.close : (c.close / eurRate)
                    }));
                    series.applyOptions({ baseValue: { type: 'price', price: lineData[0].value } });
                    series.setData(lineData);
                  }
                  lastCandleRef.current = candles[candles.length - 1];
                  chart.timeScale().fitContent();
                }).catch(() => {
                  if (chartDisposed.current || signal.aborted) return;
                  if (chartStyle === 'candlestick') {
                    series.setData(candles);
                  } else {
                    const lineData = candles.map(c => ({ time: c.time, value: c.close }));
                    series.applyOptions({ baseValue: { type: 'price', price: lineData[0].value } });
                    series.setData(lineData);
                  }
                  lastCandleRef.current = candles[candles.length - 1];
                  chart.timeScale().fitContent();
                });
            }
          }).catch(() => {});
        
        const ro = new ResizeObserver(() => { if (!chartDisposed.current) { try { chart.applyOptions({ width: chartDiv.clientWidth }); } catch {} } });
        ro.observe(chartDiv);
        
        return () => { 
          abortCtrl.abort();
          ro.disconnect(); 
          candleSeriesRef.current = null; 
          loadedSymbol.current = null;
          chartDisposed.current = true; try { chart.remove(); } catch {}
          
        };
      } catch (e) {
        console.warn('Chart error:', e);
        if (containerRef.current) containerRef.current.innerHTML = '<div style="display:flex;align-items:center;justify-content:center;height:100%;color:#9ca3af;font-size:14px;">Graphique temporairement indisponible</div>';
      }
    }

    return () => { abortCtrl.abort(); loadedSymbol.current = null; };
  }, [tvSymbol, theme, isEuronext, symbol, chartStyle, period, fullscreen]);

  // Update chart with live price
  useEffect(() => {
    if (!candleSeriesRef.current || chartDisposed.current) return;
    if (!livePrice || livePrice <= 0) return;
    try {
      const last = lastCandleRef.current;
      if (last) {
        if (chartStyle === 'candlestick') {
          candleSeriesRef.current.update({
            time: last.time,
            open: last.open,
            high: Math.max(last.high, livePrice),
            low: Math.min(last.low, livePrice),
            close: livePrice,
          });
        } else {
          candleSeriesRef.current.update({
            time: last.time,
            value: livePrice,
          });
        }
      }
    } catch (e) {
      // Chart disposed - ignore
    }
  }, [livePrice, chartStyle]);

  return (
    <div className={`tradingview-widget-container bg-[#131722] overflow-hidden ${fullscreen ? 'h-[calc(100vh-50px)]' : 'h-[400px]'}`}>
      <div ref={containerRef} className="w-full h-full" />
    </div>
  );
}

// Asset Logo Component with fallback to initials
function AssetLogo({ symbol, name, size = 'sm', showName = false }) {
  const [imgError, setImgError] = useState(false);
  const logoUrl = getAssetLogo(symbol);
  const assetName = name || ASSET_NAMES[symbol] || symbol;
  
  const sizeClasses = {
    xs: 'w-5 h-5',
    sm: 'w-8 h-8',
    md: 'w-10 h-10',
    lg: 'w-12 h-12'
  };

  const textSizeClasses = {
    xs: 'text-[9px]',
    sm: 'text-xs',
    md: 'text-sm',
    lg: 'text-base'
  };

  const getCategoryColor = (sym) => {
    if (['BTC','ETH','XRP','SOL','DOGE','ADA','DOT','LINK','AVAX','BNB','MATIC','SHIB','LTC','UNI','AAVE','XVG'].includes(sym))
      return '#F59E0B';
    if (['GOOGL','AAPL','MSFT','AMZN','TSLA','META','NVDA','NFLX','JPM','DIS'].includes(sym))
      return '#3B82F6';
    if (['MRNA','PFE','BIIB','JNJ'].includes(sym))
      return '#10B981';
    if (['GOLD','SILVER','OIL','NATGAS','WHEAT'].includes(sym))
      return '#EAB308';
    return '#6B7280';
  };
  
  const logoElement = (!logoUrl || imgError) ? (
    <div 
      className={`${sizeClasses[size]} rounded-full flex items-center justify-center text-white font-bold ${textSizeClasses[size]} flex-shrink-0 shadow-md ring-1 ring-white/10`}
      style={{ backgroundColor: ASSET_BRAND_COLORS[symbol] || getCategoryColor(symbol) }}
    >
      {symbol.slice(0, symbol.length > 3 ? 2 : 3)}
    </div>
  ) : (
    <div className={`${sizeClasses[size]} rounded-full flex-shrink-0 ring-1 ring-white/10 shadow-md overflow-hidden bg-white/10`}>
      <img 
        src={logoUrl} 
        alt={assetName}
        className="w-full h-full object-contain p-0.5"
        onError={() => setImgError(true)}
      />
    </div>
  );

  if (!showName) {
    return logoElement;
  }

  return (
    <div className="flex flex-col items-start">
      <div className="flex items-center gap-2">
        {logoElement}
        <span className="font-semibold">{symbol}</span>
      </div>
      <span className={`${textSizeClasses[size]} text-muted-foreground ml-8 -mt-0.5`}>
        {assetName}
      </span>
    </div>
  );
}

export default function TradingRoom({ marketType = 'arbitrage' }) {
  const { token, user } = useAuth();
  const navigate = useNavigate();
  const { currentTemplate } = useTheme();
  const [manualTradingAllowed, setManualTradingAllowed] = useState(false);
  
  const [selectedCategory, setSelectedCategory] = useState('crypto');
  const [assets, setAssets] = useState(FALLBACK_ASSETS);
  const [selectedAsset, setSelectedAsset] = useState(FALLBACK_ASSETS.crypto[0]);
  const [investmentAmount, setInvestmentAmount] = useState(100);
  const [tradeHistory, setTradeHistory] = useState([]);
  const [isMarketOpen, setIsMarketOpen] = useState(true);
  const [marketHoursEnabled, setMarketHoursEnabled] = useState(false);
  const [isLoadingPrices, setIsLoadingPrices] = useState(false);
  const [lastUpdate, setLastUpdate] = useState(null);
  const [priceTick, setPriceTick] = useState(0);
  const [dataSource, setDataSource] = useState('loading');
  // Trading parameters
  const [orderType, setOrderType] = useState('Market');
  const [lotSize, setLotSize] = useState(0.1);
  const [leverage, setLeverage] = useState('x1');
  const [stopLoss, setStopLoss] = useState('');
  const [takeProfit, setTakeProfit] = useState('');
  const [assetSearch, setAssetSearch] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [historyPage, setHistoryPage] = useState(1);
  const [historyTotal, setHistoryTotal] = useState(0);
  const [historyTotalPages, setHistoryTotalPages] = useState(0);
  const [historyTotalProfit, setHistoryTotalProfit] = useState(0);
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);
  const [openPositions, setOpenPositions] = useState([]);
  const [activeTradeTab, setActiveTradeTab] = useState('open');
  const [confirmOrder, setConfirmOrder] = useState(null);
  const [confirmClose, setConfirmClose] = useState(null);
  const [chartStyle, setChartStyle] = useState('candlestick');
  const [chartPeriod, setChartPeriod] = useState('1mo');
  const [chartFullscreen, setChartFullscreen] = useState(false);
  const [activeTab, setActiveTab] = useState('history');
  
  // Account metrics
  const [accountMetrics, setAccountMetrics] = useState({
    balance: 0,
    equity: 0,
    credit: 0,
    margin: 0,
    freeMargin: 0,
    marginLevel: 100,
    profit: 0,
    source: 'available',
  });

  const primaryColor = currentTemplate?.colors?.primary || '#0052FF';
  const accentColor = currentTemplate?.colors?.accent || '#10B981';
  
  // Fetch real market prices
  const [eurRate, setEurRate] = useState(1.08);
  
  const fetchMarketPrices = useCallback(async () => {
    setIsLoadingPrices(true);
    try {
      const [cryptoRes, stocksRes, commoditiesRes, forexRes, biotechRes, indicesRes, euronextRes] = await Promise.all([
        axios.get(`${API}/market/prices`).catch(() => null),
        axios.get(`${API}/market/stocks`).catch(() => null),
        axios.get(`${API}/market/commodities`).catch(() => null),
        axios.get(`${API}/market/forex`).catch(() => null),
        axios.get(`${API}/market/biotech`).catch(() => null),
        axios.get(`${API}/market/indices`).catch(() => null),
        axios.get(`${API}/market/euronext`).catch(() => null),
      ]);
      
      // Get EUR/USD rate from any response
      const rate = cryptoRes?.data?.eur_rate || stocksRes?.data?.eur_rate || forexRes?.data?.eur_rate || 1.08;
      setEurRate(rate);
      
      const newAssets = { ...FALLBACK_ASSETS };
      
      // Euronext prices are already in EUR, others need conversion
      const isEurCategory = (cat) => cat === 'euronext';
      
      const updateCategory = (res, cat) => {
        if (res?.data?.prices?.length > 0) {
          newAssets[cat] = res.data.prices.map(p => ({
            ...((FALLBACK_ASSETS[cat] || []).find(a => a.symbol === p.symbol) || {}),
            symbol: p.symbol,
            name: p.name,
            price: isEurCategory(cat) ? p.price : (p.price / rate),
            priceUsd: p.price,
            change: p.change_24h,
          }));
        }
      };

      updateCategory(cryptoRes, 'crypto');
      if (cryptoRes?.data?.source) setDataSource(cryptoRes.data.source);
      updateCategory(stocksRes, 'bourse');
      updateCategory(euronextRes, 'euronext');
      updateCategory(commoditiesRes, 'matiere');
      updateCategory(forexRes, 'devise');
      updateCategory(biotechRes, 'biotech');
      updateCategory(indicesRes, 'indices');
      
      setAssets(newAssets);
      setLastUpdate(new Date());
      
      // Don't update selectedAsset reference to avoid TradingView widget re-render
      // The price is already in assets[category] which updates the UI
      
    } catch (error) {
      console.error('Error fetching market prices:', error);
      setDataSource('fallback');
    } finally {
      setIsLoadingPrices(false);
    }
  }, []);

  // Fetch trade positions (closed/visible) with server-side pagination
  const fetchClosedPositions = useCallback(async (page = 1) => {
    if (!token || !user?.id) return;
    setIsLoadingHistory(true);
    try {
      const response = await axios.get(`${API}/trade-positions/${user.id}`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { page, limit: 10, sort_order: 'desc' }
      });
      const data = response.data;
      setTradeHistory((data.positions || []).map(p => ({
        id: p.id,
        symbol: p.symbol,
        type: p.trade_type,
        lot: p.lot,
        openPrice: p.open_price,
        closePrice: p.close_price,
        profit: p.profit,
        percentage: p.profit_percentage,
        openTime: p.open_time,
        closeTime: p.close_time,
        date: p.trade_date,
        status: p.status || 'closed',
      })));
      setHistoryTotal(data.total || 0);
      setHistoryTotalPages(data.total_pages || 0);
      setHistoryTotalProfit(data.total_profit || 0);
      setHistoryPage(page);
      
      // Update account profit
      setAccountMetrics(prev => ({
        ...prev,
        profit: data.total_profit || 0,
        equity: prev.balance + (data.total_profit || 0)
      }));
    } catch (error) {
      console.error('Failed to fetch positions:', error);
    } finally {
      setIsLoadingHistory(false);
    }
  }, [token, user?.id]);

  useEffect(() => {
    fetchClosedPositions(1);
    
    // Check market hours
    const now = new Date();
    const hour = now.getHours();
    setIsMarketOpen(hour >= 9 && hour < 22);
    
    // Check if manual trading is allowed for this client
    if (token && user?.id) {
      axios.get(`${API}/trading/manual-allowed/${user.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      }).then(res => {
        setManualTradingAllowed(res.data?.allowed ?? false);
        setMarketHoursEnabled(res.data?.market_hours_enabled ?? false);
      }).catch(() => {});

      // Load real trading balance
      axios.get(`${API}/trading/balance/${user.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      }).then(res => {
        const bal = res.data?.balance || 0;
        setAccountMetrics(prev => ({
          ...prev,
          balance: bal,
          equity: bal + prev.profit,
          freeMargin: bal,
          source: res.data?.source || 'available',
        }));
      }).catch(() => {});
    }
  }, [fetchClosedPositions, token, user?.id]);

  // Fetch market prices on mount and every 30 seconds
  useEffect(() => {
    fetchMarketPrices();
    
    const interval = setInterval(fetchMarketPrices, 5000);
    return () => clearInterval(interval);
  }, [fetchMarketPrices]);

  // Tick every 2s to keep position prices visually alive
  useEffect(() => {
    const t = setInterval(() => setPriceTick(n => n + 1), 2500);
    return () => clearInterval(t);
  }, []);

  const handleCategoryChange = (category) => {
    setSelectedCategory(category);
    const categoryAssets = assets[category];
    if (categoryAssets && categoryAssets.length > 0) {
      setSelectedAsset(categoryAssets[0]);
    }
  };

  const handleAssetSelect = (asset) => {
    setSelectedAsset(asset);
    // Recalculate lot based on current investment amount
    if (asset.price > 0 && investmentAmount > 0) {
      setLotSize(+(investmentAmount / (asset.price || 1)).toFixed(6));
    }
  };

  const handleBuy = () => {
    if (!selectedAsset || !currentPrice) return;
    if (investmentAmount <= 0) { toast.error('Entrez un montant à investir'); return; }
    if (accountMetrics.balance > 0 && investmentAmount > accountMetrics.balance) {
      const label = accountMetrics.source === 'trading' ? 'Portefeuille Trading' : 'Fonds Disponibles';
      toast.error('Fonds insuffisants', { description: `Requis: ${investmentAmount.toLocaleString('fr-FR', {minimumFractionDigits: 2})}€ — ${label}: ${accountMetrics.balance.toLocaleString('fr-FR', {minimumFractionDigits: 2})}€` });
      return;
    }
    setConfirmOrder({ type: 'BUY', symbol: selectedAsset.symbol, name: selectedAsset.name, price: realPrice, lot: lotSize, amount: investmentAmount, leverage, stopLoss, takeProfit });
  };

  const handleSell = () => {
    if (!selectedAsset || !currentPrice) return;
    if (investmentAmount <= 0) { toast.error('Entrez un montant à investir'); return; }
    if (accountMetrics.balance > 0 && investmentAmount > accountMetrics.balance) {
      const label = accountMetrics.source === 'trading' ? 'Portefeuille Trading' : 'Fonds Disponibles';
      toast.error('Fonds insuffisants', { description: `Requis: ${investmentAmount.toLocaleString('fr-FR', {minimumFractionDigits: 2})}€ — ${label}: ${accountMetrics.balance.toLocaleString('fr-FR', {minimumFractionDigits: 2})}€` });
      return;
    }
    setConfirmOrder({ type: 'SELL', symbol: selectedAsset.symbol, name: selectedAsset.name, price: realPrice, lot: lotSize, amount: investmentAmount, leverage, stopLoss, takeProfit });
  };

  const executeOrder = async () => {
    if (!confirmOrder) return;
    // Use live price at moment of confirmation (not the snapshot from click)
    const liveExecPrice = currentPrice > 0 ? currentPrice : confirmOrder.price;
    const execPrice = confirmOrder.type === 'BUY' 
      ? liveExecPrice * (1 + spreadPct) 
      : liveExecPrice * (1 - spreadPct);
    try {
      const res = await axios.post(`${API}/trade-positions/manual`, {
        symbol: confirmOrder.symbol,
        trade_type: confirmOrder.type,
        lot: confirmOrder.lot,
        amount: confirmOrder.amount,
        leverage: confirmOrder.leverage,
        open_price: parseFloat(execPrice.toFixed(2)),
        stop_loss: confirmOrder.stopLoss || null,
        take_profit: confirmOrder.takeProfit || null,
      }, { headers: { Authorization: `Bearer ${token}` } });
      setOpenPositions(prev => [res.data, ...prev]);
      setAccountMetrics(prev => ({ ...prev, balance: Math.max(0, prev.balance - confirmOrder.amount) }));
      toast.success(`${confirmOrder.type === 'BUY' ? 'Achat' : 'Vente'} ${confirmOrder.symbol} @ ${fmtPrice(execPrice)} €`);
      setConfirmOrder(null);
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); }
  };

  const handleClosePosition = (pos) => {
    // pos from livePositions already has currentPrice and pnl — keep full precision
    setConfirmClose({ ...pos, currentPrice: pos.currentPrice, pnl: pos.pnl });
  };

  const executeClosePosition = async () => {
    if (!confirmClose) return;
    // Use live price at the moment of confirmation
    const livePos = livePositions.find(p => p.id === confirmClose.id);
    const closePrice = livePos?.currentPrice || confirmClose.currentPrice;
    try {
      const res = await axios.post(`${API}/trade-positions/manual/${confirmClose.id}/close`, {
        close_price: closePrice
      }, { headers: { Authorization: `Bearer ${token}` } });
      setOpenPositions(prev => prev.filter(p => p.id !== confirmClose.id));
      // Update balance: add back investment + profit
      const closedInvestment = confirmClose.investment_amount || confirmClose.lot * confirmClose.open_price || 0;
      const investBack = closedInvestment + (res.data.profit || 0);
      setAccountMetrics(prev => ({ ...prev, balance: Math.round((prev.balance + investBack) * 100) / 100 }));
      const creditLabel = res.data.credited_to === 'trading' ? 'portefeuille trading' : 'fonds disponibles';
      toast.success(`Position fermée — ${res.data.profit >= 0 ? '+' : ''}${res.data.profit}€ (crédité: ${creditLabel})`);
      setConfirmClose(null);
      fetchClosedPositions(1);
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); }
  };

  // Fetch open positions with live simulated prices
  const fetchOpenPositions = useCallback(async () => {
    if (!token || !user?.id) return;
    try {
      const res = await axios.get(`${API}/trade-positions/${user.id}/live`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setOpenPositions((res.data || []).map(p => ({
        ...p, id: p.id, symbol: p.symbol, trade_type: p.trade_type, lot: p.lot,
        open_price: p.open_price, stop_loss: p.stop_loss, take_profit: p.take_profit,
        leverage: p.leverage || 'x1', open_time: p.open_time, trade_date: p.trade_date,
        simulated_price: p.simulated_price || null,
      })));
    } catch {}
  }, [token, user?.id]);

  useEffect(() => { fetchOpenPositions(); }, [fetchOpenPositions]);

  // Poll live positions every 3 seconds for simulated price updates
  useEffect(() => {
    if (!token || !user?.id) return;
    const interval = setInterval(fetchOpenPositions, 3000);
    return () => clearInterval(interval);
  }, [token, user?.id, fetchOpenPositions]);

  // Merge open positions with live market prices on every assets update
  // eslint-disable-next-line
  const livePositions = openPositions.map(pos => {
    let marketPrice = pos.open_price;
    for (const cat of Object.values(assets)) {
      const found = cat?.find(a => a.symbol === pos.symbol);
      if (found?.price) { marketPrice = found.price; break; }
    }
    let currentPrice = pos.simulated_price || marketPrice;
    // Micro-fluctuations when no admin simulation — biased by trading_bias (Method 4)
    if (!pos.simulated_price && marketPrice > 0) {
      const bias = (pos.trading_bias || 0) / 1000; // +5 → +0.005 drift per tick
      const seed = priceTick * 7.13 + (pos.open_price || 0) * 3.7;
      const noise = Math.sin(seed) * 0.0003 + Math.sin(seed * 2.3) * 0.0002;
      const biasAccum = bias * priceTick * 0.05; // accumulates slowly over time
      const direction = pos.trade_type === 'BUY' ? 1 : -1;
      currentPrice = marketPrice * (1 + noise + biasAccum * direction);
    }
    const lev = parseFloat((pos.leverage || 'x1').toString().replace('x', '')) || 1;
    const pnl = pos.trade_type === 'BUY'
      ? (currentPrice - pos.open_price) * pos.lot * lev
      : (pos.open_price - currentPrice) * pos.lot * lev;
    return { ...pos, currentPrice, pnl };
  });

  // Auto-close positions when SL/TP is hit
  const slTpTriggeredRef = useRef(new Set());
  useEffect(() => {
    livePositions.forEach(pos => {
      if (slTpTriggeredRef.current.has(pos.id)) return;
      const price = pos.currentPrice;
      let triggered = false;
      let reason = '';
      if (pos.stop_loss && pos.trade_type === 'BUY' && price <= pos.stop_loss) { triggered = true; reason = 'Stop Loss'; }
      if (pos.stop_loss && pos.trade_type === 'SELL' && price >= pos.stop_loss) { triggered = true; reason = 'Stop Loss'; }
      if (pos.take_profit && pos.trade_type === 'BUY' && price >= pos.take_profit) { triggered = true; reason = 'Take Profit'; }
      if (pos.take_profit && pos.trade_type === 'SELL' && price <= pos.take_profit) { triggered = true; reason = 'Take Profit'; }
      if (triggered) {
        slTpTriggeredRef.current.add(pos.id);
        axios.post(`${API}/trade-positions/manual/${pos.id}/close`, { close_price: price }, { headers: { Authorization: `Bearer ${token}` } })
          .then(res => {
            setOpenPositions(prev => prev.filter(p => p.id !== pos.id));
            const investBack = (pos.investment_amount || pos.lot * pos.open_price || 0) + (res.data.profit || 0);
            setAccountMetrics(prev => ({ ...prev, balance: Math.round((prev.balance + investBack) * 100) / 100 }));
            toast.success(`${reason} ${pos.symbol} @ ${fmtPrice(price)} € — P/L: ${res.data.profit >= 0 ? '+' : ''}${res.data.profit?.toFixed(2)} €`);
          })
          .catch(() => { slTpTriggeredRef.current.delete(pos.id); });
      }
    });
  }, [livePositions]);

  // Smart price formatting: more decimals for small prices
  const fmtPrice = (p) => {
    if (!p && p !== 0) return '—';
    if (p === 0) return '...';
    if (p < 0.01) return p.toFixed(6);
    if (p < 1) return p.toFixed(4);
    if (p < 10) return p.toFixed(3);
    return p.toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };
  const fmtPnl = (p) => {
    if (Math.abs(p) < 0.01) return p.toFixed(4);
    return p.toFixed(2);
  };

  // currentPrice reads directly from assets, no need to sync selectedAsset
  // Unified micro-fluctuation for ALL price displays
  const getLivePrice = (asset) => {
    if (!asset?.price || asset.price <= 0) return 0;
    const t = priceTick + (asset.symbol?.charCodeAt(0) || 65);
    const micro = Math.sin(t * 0.4) * 0.0003 + Math.cos(t * 0.7) * 0.0002 + Math.sin(t * 1.1) * 0.0001;
    return +(asset.price * (1 + micro)).toFixed(asset.price < 1 ? 6 : 2);
  };
  
  // Live direction indicator - alternates based on micro-fluctuations (gives visual movement)
  const getLiveDirection = (asset) => {
    if (!asset?.symbol) return 1;
    const t = priceTick + (asset.symbol?.charCodeAt(0) || 65);
    const micro = Math.sin(t * 0.4) * 0.0003 + Math.cos(t * 0.7) * 0.0002;
    // Mix real 24h change with micro direction for visual variety
    const realDir = (asset.change || 0) >= 0 ? 1 : -1;
    const microDir = micro >= 0 ? 1 : -1;
    // 70% real direction, 30% micro direction (gives occasional flips)
    return (realDir * 0.7 + microDir * 0.3) >= 0 ? 1 : -1;
  };

  const baseAsset = assets[selectedCategory]?.find(a => a.symbol === selectedAsset?.symbol);
  const realPrice = baseAsset?.price || selectedAsset?.price || 0;
  const currentPrice = getLivePrice(baseAsset || selectedAsset || {});

  // Auto-recalculate lot when price or selected asset changes
  useEffect(() => {
    if (realPrice > 0 && investmentAmount > 0) {
      setLotSize(+(investmentAmount / realPrice).toFixed(6));
    }
  }, [realPrice, selectedAsset?.symbol]);
  const currentChange = (baseAsset?.change || 0);
  // Realistic spreads by category and asset size
  const getSpreadPct = (symbol, category) => {
    // Forex majors - tightest spreads
    if (category === 'devise') {
      if (['EURUSD','GBPUSD','USDJPY','USDCHF'].includes(symbol)) return 0.00008; // 0.8 pips
      if (['AUDUSD','USDCAD','NZDUSD','EURGBP','EURJPY'].includes(symbol)) return 0.00012; // 1.2 pips
      return 0.00025; // exotic pairs ~2.5 pips
    }
    // Crypto
    if (category === 'crypto') {
      if (['BTC','ETH'].includes(symbol)) return 0.0005; // 0.05%
      if (['SOL','XRP','BNB','ADA','DOGE','DOT','AVAX','LINK'].includes(symbol)) return 0.001; // 0.1%
      return 0.002; // 0.2% small caps
    }
    // US stocks
    if (category === 'bourse') {
      if (['AAPL','MSFT','GOOGL','AMZN','TSLA','META','NVDA'].includes(symbol)) return 0.0003; // 0.03% mega caps
      if (['JPM','BAC','V','MA','WMT','KO','DIS','NFLX'].includes(symbol)) return 0.0005; // 0.05%
      return 0.001; // 0.1% smaller stocks
    }
    // Euronext
    if (category === 'euronext') {
      if (['MC','OR','RMS','TTE','SAN','AI','SU','BNP','AIR'].includes(symbol)) return 0.0005; // 0.05% CAC40 majors
      return 0.001; // 0.1%
    }
    // Indices
    if (category === 'indices') {
      if (['SPX','NDX','DJI','CAC40','DAX'].includes(symbol)) return 0.0003; // 0.03%
      return 0.0005; // 0.05%
    }
    // Commodities
    if (category === 'matiere') {
      if (['GOLD','OIL','BRENT'].includes(symbol)) return 0.0004; // 0.04%
      if (['SILVER','NATGAS','COPPER'].includes(symbol)) return 0.001; // 0.1%
      return 0.002; // 0.2% agricultural
    }
    // Biotech
    if (category === 'biotech') {
      if (['LLY','NVO','AMGN','ABBV','MRK'].includes(symbol)) return 0.0005;
      return 0.001;
    }
    return 0.001;
  };
  const spreadPct = getSpreadPct(selectedAsset?.symbol, selectedCategory);
  const spread = (currentPrice * spreadPct * 2).toFixed(2);
  const displayPrice = currentPrice < 1 ? currentPrice.toFixed(4) : currentPrice.toFixed(2);
  const askPrice = currentPrice > 0 ? currentPrice * (1 + spreadPct) : 0;
  const bidPrice = currentPrice > 0 ? currentPrice * (1 - spreadPct) : 0;
  const buyPrice = askPrice > 0 ? (askPrice < 1 ? askPrice.toFixed(4) : askPrice.toFixed(2)) : displayPrice;
  const sellPrice = bidPrice > 0 ? (bidPrice < 1 ? bidPrice.toFixed(4) : bidPrice.toFixed(2)) : displayPrice;

  return (
    <DashboardLayout hideSidebar={true}>
      <div className="space-y-4" data-testid="trading-room">
        {/* Header */}
        <div className="border-b border-border/50 bg-card/50 backdrop-blur -mx-4 -mt-4 px-4 py-4 mb-4 sticky top-0 z-10">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={() => navigate('/client')}>
              <ArrowDownRight className="w-5 h-5 rotate-[135deg]" />
            </Button>
            <div>
              <h1 className="text-2xl font-bold">{marketType === 'arbitrage' ? 'Arbitrage' : 'Salle des marchés'}</h1>
              <p className="text-muted-foreground text-sm">Tradez en temps réel sur les marchés mondiaux</p>
            </div>
          </div>
        </div>

        {/* Category Tabs */}
        <div className="flex gap-2 overflow-x-auto pb-2">
          {Object.keys(assets).map((cat) => (
            <Button
              key={cat}
              variant={selectedCategory === cat ? 'default' : 'outline'}
              size="sm"
              onClick={() => { handleCategoryChange(cat); setAssetSearch(''); }}
              style={selectedCategory === cat ? { backgroundColor: primaryColor } : {}}
              data-testid={`category-${cat}`}
            >
              {cat === 'matiere' ? 'Matières' : 
               cat === 'devise' ? 'Devises' :
               cat === 'biotech' ? 'Biotech' :
               cat === 'indices' ? 'Indices' :
               cat === 'bourse' ? 'US' :
               cat === 'euronext' ? 'Euronext' :
               cat.charAt(0).toUpperCase() + cat.slice(1)}
              <Badge variant="secondary" className="ml-1 text-[10px] h-4">{(assets[cat] || []).length}</Badge>
            </Button>
          ))}
        </div>

        {/* Main Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
          {/* Asset List Panel - 1 column */}
          <div className="lg:col-span-1">
            <Card className="bg-card/80 backdrop-blur">
              <CardContent className="p-3">
                {/* Search */}
                <div className="relative mb-2">
                  <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
                  <Input
                    type="text"
                    value={assetSearch}
                    onChange={(e) => setAssetSearch(e.target.value)}
                    placeholder="Rechercher..."
                    className="pl-7 h-8 text-xs"
                    data-testid="asset-search-input"
                  />
                </div>
                {/* Column header: Bid/Ask labels */}
                <div className="flex items-center justify-between px-1 mb-1">
                  <span className="text-[9px] text-muted-foreground uppercase tracking-wider">Actif</span>
                  <div className="flex gap-2">
                    <span className="text-[9px] text-green-500 font-semibold uppercase tracking-wider">Achat</span>
                    <span className="text-[9px] text-muted-foreground">/</span>
                    <span className="text-[9px] text-red-500 font-semibold uppercase tracking-wider">Vente</span>
                  </div>
                </div>
                {/* Scrollable list */}
                <div className="max-h-[500px] overflow-y-auto space-y-0.5 pr-1">
                  {(() => {
                    const s = assetSearch.toLowerCase();
                    // If searching, show results from ALL categories
                    const sourceList = assetSearch
                      ? Object.entries(assets).flatMap(([cat, list]) => (list || []).map(a => ({ ...a, _cat: cat })))
                      : (assets[selectedCategory] || []);
                    const filtered = sourceList.filter(a => {
                      if (!assetSearch) return true;
                      return a.symbol.toLowerCase().includes(s) || a.name.toLowerCase().includes(s);
                    });
                    return filtered.length > 0 ? filtered.map(asset => (
                      <button
                        key={`${asset._cat || selectedCategory}-${asset.symbol}`}
                        onClick={() => {
                          if (asset._cat && asset._cat !== selectedCategory) handleCategoryChange(asset._cat);
                          setSelectedAsset(asset);
                        }}
                        className={`w-full flex items-center gap-2 p-2 rounded-lg text-left transition-colors ${
                          selectedAsset.symbol === asset.symbol
                            ? 'bg-primary/15 border border-primary/30'
                            : 'hover:bg-secondary/50'
                        }`}
                        data-testid={`asset-${asset.symbol}`}
                      >
                        <AssetLogo symbol={asset.symbol} size="xs" />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-1">
                            <p className="text-xs font-semibold truncate">{asset.symbol}</p>
                            {assetSearch && asset._cat && (
                              <span className="text-[8px] text-muted-foreground bg-muted px-1 rounded">{asset._cat}</span>
                            )}
                            {getLiveDirection(asset) >= 0
                              ? <span className="animate-arrow-up text-green-500 text-[10px]">▲</span>
                              : <span className="animate-arrow-down text-red-500 text-[10px]">▼</span>
                            }
                          </div>
                          <p className="text-[10px] text-muted-foreground truncate">{asset.name}</p>
                        </div>
                        <div className="text-right flex-shrink-0">
                          {getLivePrice(asset) > 0 ? (
                            <>
                              <div className="flex items-center gap-0.5 justify-end">
                                <span className={`w-1 h-1 rounded-full ${getLiveDirection(asset) >= 0 ? 'bg-green-500' : 'bg-red-500'} animate-heartbeat`} />
                                <span className="text-[10px] font-mono text-green-400">{(getLivePrice(asset) * (1 + getSpreadPct(asset.symbol, asset._cat || selectedCategory))).toFixed(getLivePrice(asset) < 1 ? 4 : 2)}</span>
                                <span className="text-[8px] text-muted-foreground/40 mx-0.5">|</span>
                                <span className="text-[10px] font-mono text-red-400">{(getLivePrice(asset) * (1 - getSpreadPct(asset.symbol, asset._cat || selectedCategory))).toFixed(getLivePrice(asset) < 1 ? 4 : 2)}</span>
                              </div>
                              <p className={`text-[9px] font-mono text-right ${(asset.change || 0) >= 0 ? 'text-green-500/70' : 'text-red-500/70'}`}>
                                {(asset.change || 0) >= 0 ? '+' : ''}{(asset.change || 0).toFixed(2)}%
                              </p>
                            </>
                          ) : (
                            <p className="text-[10px] text-muted-foreground">...</p>
                          )}
                        </div>
                      </button>
                    )) : (
                      <p className="text-center text-xs text-muted-foreground py-4">Aucun résultat</p>
                    );
                  })()}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Chart Area - 3 columns */}
          <div className="lg:col-span-3 space-y-4">
            {/* Asset Info Bar */}
            <Card className="bg-card/80 backdrop-blur">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div>
                      <div className="flex items-center gap-2">
                        <h2 className="text-2xl font-bold">{selectedAsset.symbol}</h2>
                        <span className="flex items-center gap-1 px-2 py-0.5 rounded-full bg-red-500/20 text-red-500 text-[10px] font-bold animate-live">
                          <span className="w-1.5 h-1.5 rounded-full bg-red-500 animate-heartbeat" />
                          LIVE
                        </span>
                      </div>
                      <p className="text-sm text-muted-foreground">{selectedAsset.name}</p>
                    </div>
                    <div className="text-right">
                      <div className="flex items-center gap-2 justify-end">
                        <p className="text-3xl font-mono font-bold tracking-tight" style={{ color: primaryColor }}>
                          {displayPrice}
                          <span className="text-base ml-1 font-normal text-muted-foreground">EUR</span>
                        </p>
                      </div>
                      <div className={`flex items-center justify-end gap-1.5 mt-1 ${(currentChange || 0) >= 0 ? 'text-green-500' : 'text-red-500'}`}>
                        <span className={`px-1.5 py-0.5 rounded text-xs font-bold ${(currentChange || 0) >= 0 ? 'bg-green-500/15' : 'bg-red-500/15'}`}>
                          {(currentChange || 0) >= 0 ? '+' : ''}{(currentChange || 0)?.toFixed(2)}%
                        </span>
                        {(currentChange || 0) >= 0 
                          ? <ArrowUpRight className="w-4 h-4" /> 
                          : <ArrowDownRight className="w-4 h-4" />}
                      </div>
                    </div>
                  </div>
                  {lastUpdate && (
                    <div className="text-xs text-muted-foreground">
                      Mis à jour: {lastUpdate.toLocaleTimeString('fr-FR')}
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* TradingView Chart */}
            <Card className={`bg-card/80 backdrop-blur overflow-hidden ${chartFullscreen ? 'fixed inset-0 z-50 rounded-none' : ''}`}>
              <CardContent className="p-0">
                {/* Pro Toolbar */}
                <div className="flex items-center justify-between px-3 py-1.5 border-b border-border/30 bg-[#131722]">
                  {/* Left: Period selector */}
                  <div className="flex items-center gap-0.5">
                    {[
                      { label: '1J', value: '1d' },
                      { label: '5J', value: '5d' },
                      { label: '1M', value: '1mo' },
                      { label: '3M', value: '3mo' },
                      { label: '6M', value: '6mo' },
                      { label: '1A', value: '1y' },
                    ].map(p => (
                      <button
                        key={p.value}
                        onClick={() => setChartPeriod(p.value)}
                        className={`px-2 py-1 rounded text-[11px] font-semibold transition-all ${
                          chartPeriod === p.value 
                            ? 'bg-primary/20 text-primary' 
                            : 'text-muted-foreground hover:text-foreground hover:bg-white/5'
                        }`}
                        data-testid={`chart-period-${p.value}`}
                      >{p.label}</button>
                    ))}
                    <div className="w-px h-4 bg-border/30 mx-1.5" />
                    {/* Chart style */}
                    <button
                      onClick={() => setChartStyle('candlestick')}
                      className={`p-1.5 rounded transition-all ${chartStyle === 'candlestick' ? 'bg-primary/20 text-primary' : 'text-muted-foreground hover:text-foreground hover:bg-white/5'}`}
                      title="Bougies"
                      data-testid="chart-style-candlestick"
                    ><BarChart3 className="w-4 h-4" /></button>
                    <button
                      onClick={() => setChartStyle('baseline')}
                      className={`p-1.5 rounded transition-all ${chartStyle === 'baseline' ? 'bg-primary/20 text-primary' : 'text-muted-foreground hover:text-foreground hover:bg-white/5'}`}
                      title="Ligne"
                      data-testid="chart-style-baseline"
                    ><LineChart className="w-4 h-4" /></button>
                  </div>
                  {/* Right: Tools */}
                  <div className="flex items-center gap-0.5">
                    <button className="p-1.5 rounded text-muted-foreground hover:text-foreground hover:bg-white/5 transition-all" title="Crosshair">
                      <Crosshair className="w-4 h-4" />
                    </button>
                    <button className="p-1.5 rounded text-muted-foreground hover:text-foreground hover:bg-white/5 transition-all" title="Indicateurs">
                      <Layers className="w-4 h-4" />
                    </button>
                    <div className="w-px h-4 bg-border/30 mx-1" />
                    <button
                      onClick={() => setChartFullscreen(f => !f)}
                      className="p-1.5 rounded text-muted-foreground hover:text-foreground hover:bg-white/5 transition-all"
                      title={chartFullscreen ? 'Réduire' : 'Plein écran'}
                      data-testid="chart-fullscreen"
                    >
                      {chartFullscreen ? <Minimize2 className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
                <TradingViewWidget 
                  key={`${selectedAsset.symbol}-${chartStyle}-${chartPeriod}`}
                  symbol={selectedAsset.symbol} 
                  theme="dark"
                  livePrice={currentPrice}
                  chartStyle={chartStyle}
                  period={chartPeriod}
                  fullscreen={chartFullscreen}
                />
                {chartFullscreen && (
                  <div className="absolute top-2 left-3 z-20 flex items-center gap-2">
                    <span className="text-lg font-bold text-white">{selectedAsset.symbol}</span>
                    <span className="text-sm text-muted-foreground">{selectedAsset.name}</span>
                    <span className="font-mono text-lg font-bold" style={{ color: primaryColor }}>{displayPrice} €</span>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Positions */}
            <Card className="bg-card/80 backdrop-blur">
              <CardHeader className="pb-2">
                <div className="flex items-center gap-2">
                  <Button variant={activeTradeTab === 'open' ? 'default' : 'ghost'} size="sm"
                    onClick={() => setActiveTradeTab('open')} data-testid="tab-open-positions">
                    Positions ouvertes <Badge className="ml-1" variant="secondary">{livePositions.length}</Badge>
                  </Button>
                  <Button variant={activeTradeTab === 'history' ? 'default' : 'ghost'} size="sm"
                    onClick={() => setActiveTradeTab('history')} data-testid="tab-history">
                    Historique <Badge className="ml-1" variant="secondary">{historyTotal}</Badge>
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {activeTradeTab === 'open' ? (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm" data-testid="open-positions-table">
                      <thead>
                        <tr className="border-b border-border text-muted-foreground text-xs">
                          <th className="text-left py-2 px-2">Symbol</th>
                          <th className="text-left py-2 px-2">Type</th>
                          <th className="text-right py-2 px-2">Lot</th>
                          <th className="text-right py-2 px-2">Prix ouv.</th>
                          <th className="text-right py-2 px-2">Prix act.</th>
                          <th className="text-right py-2 px-2">P&L</th>
                          <th className="text-right py-2 px-2">SL/TP</th>
                          <th className="text-center py-2 px-2"></th>
                        </tr>
                      </thead>
                      <tbody>
                        {livePositions.length === 0 ? (
                          <tr><td colSpan={8} className="text-center py-8 text-muted-foreground">Aucune position ouverte</td></tr>
                        ) : livePositions.map(pos => {
                          return (
                            <tr key={pos.id} className="border-b border-border/30 hover:bg-secondary/20" data-testid={`open-pos-${pos.id}`}>
                              <td className="py-2 px-2"><AssetLogo symbol={pos.symbol} size="xs" showName={true} /></td>
                              <td className="py-2 px-2">
                                <Badge className={pos.trade_type === 'BUY' ? 'bg-green-600' : 'bg-red-600'}>{pos.trade_type === 'BUY' ? 'Achat' : 'Vente'}</Badge>
                              </td>
                              <td className="py-2 px-2 text-right font-mono text-xs">{pos.lot}</td>
                              <td className="py-2 px-2 text-right font-mono text-xs">{fmtPrice(pos.open_price)} €</td>
                              <td className="py-2 px-2 text-right font-mono text-xs">{fmtPrice(pos.currentPrice)} €</td>
                              <td className={`py-2 px-2 text-right`}>
                                <span className={`font-mono text-xs font-bold ${pos.pnl >= 0 ? 'text-green-400 animate-pulse' : 'text-red-400 animate-pulse'}`}>
                                {pos.pnl >= 0 ? '+' : ''}{fmtPnl(pos.pnl)} €
                                </span>
                              </td>
                              <td className="py-2 px-2 text-right text-[10px] text-muted-foreground">
                                {pos.stop_loss ? `SL:${pos.stop_loss}` : ''} {pos.take_profit ? `TP:${pos.take_profit}` : ''}
                              </td>
                              <td className="py-2 px-2 text-center">
                                <Button size="sm" variant="outline" className="h-6 px-2 text-[10px] text-red-500 border-red-500"
                                  onClick={() => handleClosePosition(pos)} data-testid={`close-pos-${pos.id}`}>
                                  Fermer
                                </Button>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  /* History tab */
                  <div>
                    <div className="flex items-center gap-2 text-xs mb-2">
                      <span className="text-muted-foreground">Du</span>
                      <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="h-7 w-32 text-xs" data-testid="history-date-from" />
                      <span className="text-muted-foreground">au</span>
                      <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="h-7 w-32 text-xs" data-testid="history-date-to" />
                      {(dateFrom || dateTo) && (
                        <>
                          <Button variant="ghost" size="sm" className="h-7 px-2 text-xs" onClick={() => { setDateFrom(''); setDateTo(''); }}>Effacer</Button>
                          {(() => {
                            const filtered = tradeHistory.filter(t => {
                              if (!dateFrom && !dateTo) return true;
                              const parts = t.date?.split('/');
                              if (!parts || parts.length !== 3) return true;
                              const d = `${parts[2]}-${parts[1]}-${parts[0]}`;
                              if (dateFrom && d < dateFrom) return false;
                              if (dateTo && d > dateTo) return false;
                              return true;
                            });
                            const gain = filtered.reduce((s, t) => s + (t.profit || 0), 0);
                            return <span className={`text-xs font-bold ${gain >= 0 ? 'text-emerald-500' : 'text-red-500'}`}>Gain: {gain >= 0 ? '+' : ''}{gain.toFixed(2)} €</span>;
                          })()}
                        </>
                      )}
                    </div>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm" data-testid="trade-history-table">
                        <thead>
                          <tr className="border-b border-border text-muted-foreground text-xs">
                            <th className="text-left py-2 px-2">Symbol</th>
                            <th className="text-left py-2 px-2">Type</th>
                            <th className="text-left py-2 px-2">Date</th>
                            <th className="text-right py-2 px-2">Lot</th>
                            <th className="text-right py-2 px-2">Open</th>
                            <th className="text-right py-2 px-2">Close</th>
                            <th className="text-right py-2 px-2">Profit</th>
                            <th className="text-right py-2 px-2">%</th>
                          </tr>
                        </thead>
                        <tbody>
                          {tradeHistory.length === 0 ? (
                            <tr><td colSpan={8} className="text-center py-8 text-muted-foreground">Aucune position fermée</td></tr>
                          ) : tradeHistory.filter(trade => {
                            if (!dateFrom && !dateTo) return true;
                            const parts = trade.date?.split('/');
                            if (!parts || parts.length !== 3) return true;
                            const d = `${parts[2]}-${parts[1]}-${parts[0]}`;
                            if (dateFrom && d < dateFrom) return false;
                            if (dateTo && d > dateTo) return false;
                            return true;
                          }).map(trade => (
                            <tr key={trade.id} className="border-b border-border/30 hover:bg-secondary/20" data-testid={`history-row-${trade.id}`}>
                              <td className="py-2 px-2"><AssetLogo symbol={trade.symbol} size="xs" showName={true} /></td>
                              <td className="py-2 px-2"><Badge className={trade.type === 'BUY' ? 'bg-green-600' : 'bg-red-600'}>{trade.type === 'BUY' ? 'Achat' : 'Vente'}</Badge></td>
                              <td className="py-2 px-2 text-muted-foreground text-xs">{trade.date}</td>
                              <td className="py-2 px-2 text-right font-mono text-xs">{trade.lot}</td>
                              <td className="py-2 px-2 text-right font-mono text-xs">{trade.openPrice}</td>
                              <td className="py-2 px-2 text-right font-mono text-xs">{trade.closePrice || '-'}</td>
                              <td className="py-2 px-2 text-right font-mono text-xs" style={{ color: trade.profit >= 0 ? accentColor : '#ef5350' }}>
                                {trade.profit >= 0 ? '+' : ''}{trade.profit?.toFixed(2)} €
                              </td>
                              <td className="py-2 px-2 text-right font-mono text-xs" style={{ color: trade.percentage >= 0 ? accentColor : '#ef5350' }}>
                                {trade.percentage?.toFixed(2)}%
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    {historyTotalPages > 1 && (
                      <div className="flex items-center justify-between pt-3 border-t mt-2">
                        <p className="text-xs text-muted-foreground">Page {historyPage} / {historyTotalPages}</p>
                        <div className="flex gap-2">
                          <Button variant="outline" size="sm" disabled={historyPage <= 1} onClick={() => fetchClosedPositions(historyPage - 1)}>Précédent</Button>
                          <Button variant="outline" size="sm" disabled={historyPage >= historyTotalPages} onClick={() => fetchClosedPositions(historyPage + 1)}>Suivant</Button>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Right - Trading Panel */}
          <div className="lg:col-span-1 space-y-4">
            <Card className="bg-card/80 backdrop-blur">
              <CardContent className="p-4 space-y-4">
                {/* Order Type */}
                <div>
                  <Label className="text-xs uppercase text-muted-foreground">Type d&apos;ordre</Label>
                  <div className="grid grid-cols-2 gap-2 mt-1">
                    {['Market', 'Limit'].map(type => (
                      <Button key={type} variant={orderType === type ? 'default' : 'outline'} size="sm"
                        onClick={() => setOrderType(type)} className="text-sm h-9" data-testid={`order-type-${type.toLowerCase()}`}>
                        {type}
                      </Button>
                    ))}
                  </div>
                </div>

                {/* Montant à investir */}
                <div>
                  <Label className="text-xs uppercase text-muted-foreground">Montant à investir (€)</Label>
                  <div className="flex items-center gap-2 mt-1">
                    <Input type="number" value={investmentAmount}
                      onChange={(e) => {
                        const val = parseFloat(e.target.value) || 0;
                        setInvestmentAmount(val);
                        if (realPrice > 0) setLotSize(+(val / realPrice).toFixed(6));
                      }}
                      placeholder="1000" className="text-center font-mono text-sm h-10 [&::-webkit-inner-spin-button]:appearance-none" data-testid="investment-amount-input" />
                  </div>
                  <div className="flex gap-1.5 mt-1.5">
                    {[100, 250, 500, 1000, 5000].map(v => (
                      <button key={v} onClick={() => {
                        setInvestmentAmount(v);
                        if (realPrice > 0) setLotSize(+(v / realPrice).toFixed(6));
                      }}
                        className={`flex-1 text-xs py-1.5 rounded border font-medium ${investmentAmount === v ? 'border-primary bg-primary/20 text-primary' : 'border-border/50 text-muted-foreground hover:border-primary/30'}`}>
                        {v}€
                      </button>
                    ))}
                  </div>
                  <p className="text-[10px] text-muted-foreground mt-1 text-center">
                    = {lotSize} lot de {selectedAsset?.symbol || '—'}
                  </p>
                </div>

                {/* Leverage */}
                <div>
                  <Label className="text-xs uppercase text-muted-foreground">Levier</Label>
                  <div className="flex gap-1.5 mt-1">
                    {['x1', 'x2', 'x5', 'x10', 'x20'].map(lev => (
                      <button key={lev} onClick={() => setLeverage(lev)}
                        className={`flex-1 text-xs py-2 rounded border font-semibold ${leverage === lev ? 'border-primary bg-primary/20 text-primary' : 'border-border/50 text-muted-foreground hover:border-primary/30'}`}>
                        {lev}
                      </button>
                    ))}
                  </div>
                </div>

                {/* SL / TP */}
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs uppercase text-red-400">Stop Loss</Label>
                    <Input type="number" value={stopLoss} onChange={(e) => setStopLoss(e.target.value)}
                      placeholder="SL" className="font-mono text-sm h-9 mt-1 border-red-500/30 [&::-webkit-inner-spin-button]:appearance-none" data-testid="stop-loss-input" />
                  </div>
                  <div>
                    <Label className="text-xs uppercase text-green-400">Take Profit</Label>
                    <Input type="number" value={takeProfit} onChange={(e) => setTakeProfit(e.target.value)}
                      placeholder="TP" className="font-mono text-sm h-9 mt-1 border-green-500/30 [&::-webkit-inner-spin-button]:appearance-none" data-testid="take-profit-input" />
                  </div>
                </div>

                {/* Summary */}
                <div className="p-3 rounded bg-muted/30 text-xs space-y-1">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">{accountMetrics.source === 'trading' ? 'Portefeuille Trading' : 'Fonds Disponibles'}</span>
                    <span className="font-mono font-bold" style={{ color: primaryColor }}>{accountMetrics.balance.toLocaleString('fr-FR', { minimumFractionDigits: 2 })} €</span>
                  </div>
                  <div className="flex justify-between"><span className="text-muted-foreground">Montant</span><span className="font-mono">{investmentAmount.toLocaleString('fr-FR')} €</span></div>
                  <div className="flex justify-between"><span className="text-muted-foreground">Lot</span><span className="font-mono">{lotSize}</span></div>
                  <div className="flex justify-between"><span className="text-muted-foreground">Levier</span><span className="font-mono">{leverage}</span></div>
                  <div className="flex justify-between"><span className="text-muted-foreground">Exposition</span><span className="font-mono">{(investmentAmount * parseFloat(leverage.replace('x',''))).toLocaleString('fr-FR', { minimumFractionDigits: 2 })} €</span></div>
                </div>

                {/* ACHETER / VENDRE */}
                <div className="grid grid-cols-2 gap-3 pt-3 border-t border-border/30">
                  <Button onClick={handleBuy} disabled={!manualTradingAllowed || (marketHoursEnabled && !isMarketOpen)}
                    className="h-14 flex flex-col bg-green-600 hover:bg-green-700 disabled:opacity-40 animate-glow-green" data-testid="buy-btn">
                    <span className="text-base font-bold">ACHETER</span>
                    <span className="text-xs font-mono">{buyPrice} €</span>
                  </Button>
                  <Button onClick={handleSell} disabled={!manualTradingAllowed || (marketHoursEnabled && !isMarketOpen)}
                    className="h-14 flex flex-col bg-red-600 hover:bg-red-700 disabled:opacity-40 animate-glow-red" data-testid="sell-btn">
                    <span className="text-base font-bold">VENDRE</span>
                    <span className="text-xs font-mono">{sellPrice} €</span>
                  </Button>
                </div>
                {!manualTradingAllowed && (
                  <p className="text-xs text-center text-muted-foreground mt-1">Trading manuel désactivé par l'administrateur</p>
                )}
                {manualTradingAllowed && marketHoursEnabled && !isMarketOpen && (
                  <p className="text-xs text-center text-muted-foreground mt-1">Marché fermé — ouverture de 9h à 22h</p>
                )}
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Order Confirmation Dialog */}
        <Dialog open={!!confirmOrder} onOpenChange={() => setConfirmOrder(null)}>
          <DialogContent className="max-w-sm" data-testid="confirm-order-modal">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                Confirmer l&apos;ordre
              </DialogTitle>
            </DialogHeader>
            {confirmOrder && (
              <div className="space-y-4 py-2">
                <div className="p-4 rounded-lg bg-muted/30 space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-muted-foreground">Actif</span>
                    <span className="font-bold">{confirmOrder.symbol} — {confirmOrder.name}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-muted-foreground">Type</span>
                    <Badge className={confirmOrder.type === 'BUY' ? 'bg-green-600' : 'bg-red-600'}>{confirmOrder.type === 'BUY' ? 'Achat' : 'Vente'}</Badge>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-muted-foreground">Prix</span>
                    <span className="font-mono font-bold animate-pulse">{fmtPrice(currentPrice)} €</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-muted-foreground">Volume</span>
                    <span className="font-mono">{confirmOrder.amount?.toLocaleString('fr-FR')} € ({confirmOrder.lot} lot)</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-muted-foreground">Levier</span>
                    <span className="font-mono">{confirmOrder.leverage}</span>
                  </div>
                  {confirmOrder.stopLoss && (
                    <div className="flex items-center justify-between">
                      <span className="text-red-400">Stop Loss</span>
                      <span className="font-mono">{confirmOrder.stopLoss} €</span>
                    </div>
                  )}
                  {confirmOrder.takeProfit && (
                    <div className="flex items-center justify-between">
                      <span className="text-green-400">Take Profit</span>
                      <span className="font-mono">{confirmOrder.takeProfit} €</span>
                    </div>
                  )}
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" className="flex-1" onClick={() => setConfirmOrder(null)}>Annuler</Button>
                  <Button 
                    className={`flex-1 ${confirmOrder.type === 'BUY' ? 'bg-green-600 hover:bg-green-700' : 'bg-red-600 hover:bg-red-700'}`}
                    onClick={executeOrder}
                    data-testid="confirm-execute-order"
                  >
                    Confirmer {confirmOrder.type === 'BUY' ? "l'achat" : 'la vente'}
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Close Position Confirmation Dialog */}
        <Dialog open={!!confirmClose} onOpenChange={() => setConfirmClose(null)}>
          <DialogContent className="max-w-sm" data-testid="confirm-close-modal">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                Confirmer la fermeture
              </DialogTitle>
            </DialogHeader>
            {confirmClose && (() => {
              // Get live price from livePositions (with market price + fluctuations)
              const livePos = livePositions.find(p => p.id === confirmClose.id);
              const livePrice = livePos?.currentPrice || confirmClose.currentPrice;
              const livePnl = livePos?.pnl || confirmClose.pnl;
              return (
              <div className="space-y-4 py-2">
                <div className="p-4 rounded-lg bg-muted/30 space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-muted-foreground">Actif</span>
                    <span className="font-bold">{confirmClose.symbol}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-muted-foreground">Type</span>
                    <Badge className={confirmClose.trade_type === 'BUY' ? 'bg-green-600' : 'bg-red-600'}>{confirmClose.trade_type === 'BUY' ? 'Achat' : 'Vente'}</Badge>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-muted-foreground">Prix d&apos;ouverture</span>
                    <span className="font-mono">{fmtPrice(confirmClose.open_price)} €</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-muted-foreground">Prix actuel</span>
                    <span className="font-mono font-bold animate-pulse">{fmtPrice(livePrice)} €</span>
                  </div>
                  <div className="flex items-center justify-between border-t border-border/30 pt-2">
                    <span className="text-muted-foreground">Gain / Perte</span>
                    <span className={`font-mono font-bold animate-pulse ${livePnl >= 0 ? 'text-green-500' : 'text-red-500'}`}>
                      {livePnl >= 0 ? '+' : ''}{fmtPnl(livePnl)} €
                    </span>
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" className="flex-1" onClick={() => setConfirmClose(null)} data-testid="cancel-close-btn">Annuler</Button>
                  <Button 
                    className="flex-1 bg-red-600 hover:bg-red-700"
                    onClick={executeClosePosition}
                    data-testid="confirm-close-btn"
                  >
                    Confirmer la fermeture
                  </Button>
                </div>
              </div>
              );
            })()}
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
